/**
 * DoctorLandingPage — public `/dr/:slug`
 * iter100.7 (2026-02-28): Practo-inspired directory layout.
 *   • Info-dense identity card at the top (photo · name · credentials · CTA)
 *   • Sticky sub-nav tabs (Info · Smile Preview · Book · Clinic)
 *   • Two-column body: content left, sticky booking sidebar right
 *   • Preserves Invisique differentiators — AI Smile Simulator card,
 *     Certified Invisique™ Provider badge, cover image band, accent tokens,
 *     clinic photo gallery. Feels familiar (Practo-shaped) yet distinctly
 *     Invisique because of the simulator, brand credential, and warm palette.
 *
 * Booking hand-off: patient picks a slot in the right sidebar and lands on
 * /book/:doctorId?pre_date=&pre_time=&ref=landing which BookAppointment.jsx
 * already understands (no change to the OTP-persist flow).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Sparkle, ShieldCheck, SealCheck, CheckCircle, MapPin, Buildings,
    GraduationCap, Phone, WhatsappLogo, Warning, ArrowRight, Clock,
    Translate, Certificate, Stethoscope, User, CaretRight, CaretLeft,
    CurrencyInr, X, PaperPlaneRight, Sun, Moon, Coffee, ClipboardText,
    PhoneCall, Newspaper, Star, ChatCircleText, Quotes, Article,
    CalendarBlank, ShareNetwork, Funnel, Ruler,
    // iter100.42 — Trust chip icons (dynamic chips from the backend).
    Trophy, UsersFour, Baby, Lightning, Wheelchair, GenderFemale,
    FirstAid, House, CreditCard, Rainbow, Medal, ScanSmiley, Calendar,
    // iter100.57 — Additional icons for the 24-key badge catalog.
    Diamond, Heart, ChatCircle, ArrowsClockwise, BookOpen, Rocket, TestTube,
    // iter100.63 — Facility-tile icons for the redesigned Clinic tab.
    Car, VideoCamera, Toilet, WifiHigh, NavigationArrow,
    // iter100.204 — Growth Partner badge icon (referrer recognition).
    Handshake,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { format, addDays, isSameDay, parseISO } from "date-fns";
import { toast } from "sonner";
import { portalUrl } from "@/lib/host-mode";
import SmileSimulator from "@/components/site/SmileSimulator";
import ShareProfileModal from "@/components/site/ShareProfileModal";
import { BeforeAfterSlider } from "@/components/site/BeforeAfterSlider";
import SmileAssessment from "@/pages/SmileAssessment";

/* ---- Accent palette (blue is default — mirrors Practo, but our accent
       tokens let each doctor lightly personalize their page.) ---- */
const ACCENTS = {
    teal:  { base: "hsl(192 90% 33%)",  soft: "hsl(192 90% 96%)",  ring: "hsl(192 90% 88%)",  text: "hsl(192 90% 25%)" },
    rose:  { base: "hsl(340 75% 45%)",  soft: "hsl(340 75% 97%)",  ring: "hsl(340 75% 90%)",  text: "hsl(340 75% 35%)" },
    amber: { base: "hsl(28 88% 45%)",   soft: "hsl(28 88% 97%)",   ring: "hsl(28 88% 88%)",   text: "hsl(28 88% 35%)" },
    slate: { base: "hsl(215 60% 32%)",  soft: "hsl(215 60% 97%)",  ring: "hsl(215 60% 88%)",  text: "hsl(215 60% 25%)" },
};
const pickAccent = (n) => ACCENTS[n] || ACCENTS.teal;

// iter100.259 — Derive a warm short name ("Dr. Sunny") from the full
// registered name ("Dr. Sunny Rathod"). Used to personalize empty-state
// copy on the public landing page ("Dr. Sunny hasn't published…" instead
// of the generic "This provider hasn't…"). Returns null when no name is
// available so callers can fall back to their original wording.
const shortDoctorName = (fullName) => {
    if (!fullName) return null;
    const clean = String(fullName).trim().replace(/^(dr\.?|doctor)\s+/i, "");
    const first = clean.split(/\s+/)[0];
    return first ? `Dr. ${first}` : null;
};

/* -------- Section anchors used by sticky sub-nav (Practo pattern) -------- */
const SECTIONS = [
    { id: "info",         label: "Info",              icon: User },
    { id: "testimonials", label: "Reviews",           icon: ChatCircleText },
    { id: "articles",     label: "Articles",          icon: Newspaper },
    { id: "case_studies", label: "Case Studies",      icon: Article },
    { id: "clinic",       label: "Clinic",            icon: Buildings },
    { id: "simulate",     label: "Smile Preview",     icon: Sparkle },
    { id: "assessment",   label: "Smile Assessment",  icon: ClipboardText },
];


/* LandingComingSoon — iter100.282
 *
 * Friendly "not-live-yet" preview state shown when a doctor exists but
 * their profile isn't published (draft / pending). Replaces the scary
 * generic "This page isn't available" for in-progress providers.
 *
 * If the current visitor is the doctor themselves (detected via the
 * `ca_token` in localStorage matching this doctor_id via a lightweight
 * /auth/me lookup), we swap the copy to be first-person: "Your page
 * isn't live yet." + a Complete-my-profile CTA. Otherwise we show a
 * warm third-person coming-soon note.
 */
function LandingComingSoon({ detail, slug }) {
    // iter100.313 — Owner state starts UNKNOWN (null) so we don't flash
    // the third-person "Dr. X is setting up their page" copy for half a
    // second before the /doctor/profile check resolves and we swap to
    // the first-person "Your page isn't live yet." Split into 3 states:
    //   null   → still checking (render nothing / subtle skeleton)
    //   true   → doctor themselves is viewing
    //   false  → any other visitor (patient / logged-out / other doctor)
    // If no `ca_token` exists in localStorage, we can jump straight to
    // `false` without a network round-trip.
    const [isOwnerViewing, setIsOwnerViewing] = useState(() => {
        try {
            return typeof window !== "undefined" && window.localStorage.getItem("ca_token")
                ? null
                : false;
        } catch {
            return false;
        }
    });
    useEffect(() => {
        if (isOwnerViewing !== null) return undefined; // already resolved
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/doctor/profile");
                if (!cancelled) {
                    setIsOwnerViewing(!!(data?.id && data.id === detail.doctor_id));
                }
            } catch {
                if (!cancelled) setIsOwnerViewing(false);
            }
        })();
        return () => { cancelled = true; };
    }, [detail.doctor_id, isOwnerViewing]);

    const pct = Math.max(0, Math.min(100, detail.completeness_score || 0));
    const readableName = detail.doctor_name || "This provider";
    const shortName = /^dr\.?\s/i.test(readableName)
        ? `Dr. ${readableName.replace(/^dr\.?\s*/i, "").split(/\s+/)[0]}`
        : readableName;

    return (
        <div
            className="min-h-[80vh] bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-5 py-14"
            data-testid="doctor-landing-not-published"
        >
            <div className="w-full max-w-xl bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 text-center shadow-sm">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                    <Sparkle size={26} weight="fill" className="text-primary" />
                </div>
                {isOwnerViewing === null ? (
                    /* iter100.313 — Owner check still resolving. Render a
                       minimal skeleton (invisible-ish) instead of flashing
                       the wrong copy for half a second. */
                    <div className="min-h-[220px] flex items-center justify-center" data-testid="landing-preview-loading">
                        <div className="h-6 w-40 rounded bg-slate-100 animate-pulse" />
                    </div>
                ) : isOwnerViewing ? (
                    <>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                            Your page isn&apos;t live yet.
                        </h1>
                        <p className="text-muted-foreground mt-3 text-sm sm:text-base leading-relaxed">
                            You&apos;re <strong className="text-foreground">{pct}%</strong> done. Complete the essentials — a photo, your story, a clinic address, contact numbers — and your page goes live automatically.
                        </p>
                        {/* Progress bar */}
                        <div className="mt-6 h-2 rounded-full bg-slate-100 overflow-hidden max-w-sm mx-auto">
                            <div
                                className="h-full rounded-full bg-primary transition-all"
                                style={{ width: `${pct}%` }}
                                data-testid="landing-preview-progress"
                            />
                        </div>
                        <div className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                            {pct >= 80 ? "Almost there · publish from your dashboard" : `${Math.max(0, 80 - pct)}% more to go live`}
                        </div>
                        <div className="mt-7 flex items-center justify-center gap-2 flex-wrap">
                            <Link
                                to="/doctor/landing-page"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors"
                                data-testid="landing-preview-cta-editor"
                            >
                                Continue setting up
                                <ArrowRight size={14} weight="bold" />
                            </Link>
                            <Link
                                to="/doctor"
                                className="inline-flex items-center h-11 px-5 rounded-full border border-border text-sm font-semibold text-foreground hover:bg-slate-50 transition-colors"
                                data-testid="landing-preview-cta-dashboard"
                            >
                                Back to dashboard
                            </Link>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-6">
                            Public URL previewed: <code className="bg-slate-100 rounded px-1.5 py-0.5 text-[10.5px]">/dr/{slug}</code>
                        </p>
                    </>
                ) : (
                    <>
                        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                            {shortName} is setting up their page.
                        </h1>
                        <p className="text-muted-foreground mt-3 text-sm sm:text-base leading-relaxed">
                            This provider&apos;s public profile isn&apos;t live yet. Check back shortly, or browse other Invisique-certified doctors.
                        </p>
                        <div className="mt-7 flex items-center justify-center gap-2 flex-wrap">
                            <Link
                                to="/find-doctor"
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors"
                            >
                                Browse other providers
                                <ArrowRight size={14} weight="bold" />
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}



export default function DoctorLandingPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSection, setActiveSection] = useState("info");
    /* iter100.16 — Booking modal state lives in the parent so both the
       hero-band CTA and the sidebar's "Book Appointment" button open the
       SAME modal (no more sidebar-picker duplication). */
    const [bookingOpen, setBookingOpen] = useState(false);
    /* iter100.166 — Sticky mobile CTA. We observe the identity card
       (contains the primary "Book Appointment" pill). When it leaves the
       viewport on mobile, we surface a fixed bottom bar with a single
       "Book Appointment" button so patients always have a one-tap path
       to conversion. Desktop is unaffected (sidebar handles CTA
       persistence). */
    const identityCardRef = useRef(null);
    const [showStickyCTA, setShowStickyCTA] = useState(false);
    useEffect(() => {
        const el = identityCardRef.current;
        if (!el || typeof IntersectionObserver === "undefined") return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Show the sticky bar once the identity card has fully scrolled
                // above the fold. `isIntersecting` flips to false the instant
                // any pixel leaves the viewport — we want to hide the bar while
                // even a sliver of the primary CTA pill is still visible.
                setShowStickyCTA(!entry.isIntersecting && entry.boundingClientRect.top < 0);
            },
            { threshold: 0, rootMargin: "0px 0px -40px 0px" },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [doc]);

    /* iter100.167 — Sticky mobile CTA micro-label. Reuses the existing
       useNextAvailable hook so the sticky bar can show "Available today"
       (with a pulsing green dot) or "Next: {day · time}". Called
       unconditionally before any early returns to satisfy rules-of-hooks;
       the hook itself no-ops when `doctorId` is falsy. */
    const nextSlotLabel = useNextAvailable(doc?.doctor_id);
    const isAvailableToday = !!nextSlotLabel && nextSlotLabel.startsWith(format(new Date(), "EEE d MMM"));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/public/doctor-page/${slug}`);
                if (cancelled) return;
                /* iter100.29 — Slug redirect. If the doctor renamed their
                   URL, the backend still resolves the OLD slug but returns
                   `canonical_slug` set to the new one. We navigate to the
                   canonical URL with `replace: true` so the user's history
                   doesn't get polluted with the deprecated URL. Old QR
                   codes / printed cards / shared links keep working. */
                if (data?.canonical_slug && data.canonical_slug !== slug) {
                    navigate(`/dr/${data.canonical_slug}`, { replace: true });
                    return;
                }
                setDoc(data);
                api.post("/public/doctor-page/event", { kind: "view", slug }).catch(() => {});
            } catch (err) {
                if (!cancelled) {
                    const status = err?.response?.status;
                    // iter100.282 — the axios interceptor now preserves the
                    // raw structured detail on `detail_raw`. Fall back to
                    // `detail` in case an older backend deploy still sends
                    // a plain object.
                    const raw = err?.response?.data?.detail_raw
                        || (typeof err?.response?.data?.detail === "object" ? err.response.data.detail : null);
                    if (status === 404 && raw && raw.code === "not_published_yet") {
                        setError({ kind: "not_published", ...raw });
                    } else if (status === 404) {
                        setError({ kind: "not_found" });
                    } else {
                        setError({ kind: "error" });
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug, navigate]);

    /* iter100.10 — Sub-nav behaves as a tab-switcher (not a scroll anchor).
       Each tab renders its own content in the LEFT column while the identity
       band, CTA strip, and booking sidebar stay visible. Patients stay
       inside the doctor profile the whole time. Default tab is "info". */
    useEffect(() => {
        // Reset to Info when the slug changes (new doctor navigated to)
        setActiveSection("info");
    }, [slug]);

    /* iter100.48 — Open booking modal when arriving with `#book` in the URL.
       The article + case-study pages send readers here via `/dr/:slug#book`
       so their "Book appointment" CTAs actually trigger booking. Runs after
       `doc` loads (the modal depends on doctor data). The hash is cleared
       so a page refresh doesn't re-open the modal. */
    useEffect(() => {
        if (!doc) return;
        if (typeof window !== "undefined" && window.location.hash === "#book") {
            setBookingOpen(true);
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
        }
    }, [doc]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground text-sm" data-testid="doctor-landing-loading">
                Loading…
            </div>
        );
    }
    if (error?.kind === "not_published") {
        return <LandingComingSoon detail={error} slug={slug} />;
    }
    if (error?.kind === "not_found" || (!doc && !loading)) {
        return (
            <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="doctor-landing-not-found">
                <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                    <Warning size={28} weight="duotone" color="hsl(38 92% 50%)" />
                </div>
                <h1 className="font-display text-2xl font-bold">This page isn&apos;t available.</h1>
                <p className="text-muted-foreground mt-2">The link you followed might be broken, or the provider has moved.</p>
                <Link to="/find-doctor" className="inline-block mt-6 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
                    Browse other providers →
                </Link>
            </div>
        );
    }
    if (!doc) return null;

    const accent = pickAccent(doc.accent);
    const clinic = doc.primary_clinic || {};
    const displayName = doc.name || "";
    const firstName = (() => {
        const parts = displayName.split(/\s+/).filter(Boolean);
        return (parts[0]?.replace(/\.$/, "").toLowerCase() === "dr" ? parts[1] : parts[0]) || "the doctor";
    })();
    /* iter100.20 — Public-facing short name that preserves the doctor
       honorific. e.g. "Dr. James Wilson" → "Dr. James". Used in the
       "About" heading, simulator subtext and WhatsApp greeting so
       patients see the proper title — the earlier bare "James" felt
       overly casual for a certified provider surface. */
    const shortName = /^dr\.?\s/i.test(displayName) ? `Dr. ${firstName}` : firstName;

    return (
        <div className="bg-slate-50 min-h-screen" style={{ "--accent-base": accent.base, "--accent-soft": accent.soft, "--accent-ring": accent.ring }} data-testid="doctor-landing-page">

            {/* =====================================================================
                IDENTITY BAND — sits atop a subtle cover band (Practo does a solid
                colour, we do cover image + gradient fallback so doctors who upload
                a hero get a personal touch). iter100.11 — the 3 lead-gen quick
                actions live INSIDE the identity card now (as colored pills), so
                the sub-nav tabs get more visual dominance below.
               ===================================================================== */}
            <IdentityBand
                doc={doc}
                slug={slug}
                accent={accent}
                clinic={clinic}
                activeSection={activeSection}
                onOpenSimulator={() => setActiveSection("simulate")}
                onOpenAssessment={() => setActiveSection("assessment")}
                onOpenBooking={() => setBookingOpen(true)}
                cardRef={identityCardRef}
            />

            {/* =====================================================================
                STICKY SUB-NAV — mirrors Practo's in-page tabs (Info · Feed · Reviews
                · Stories). iter100.10 — now a tab-switcher: clicking swaps the
                LEFT column content, no scroll, no navigation. Patient stays on
                the doctor profile the whole time.
               ===================================================================== */}
            <StickySubNav accent={accent} activeSection={activeSection} onSelect={setActiveSection} />

            {/* =====================================================================
                TWO-COLUMN BODY — LEFT switches by tab. RIGHT (booking sidebar)
                is always visible so the primary CTA never leaves the fold.
               ===================================================================== */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6 lg:gap-8">

                {/* LEFT column (tabbed content) */}
                <main className="lg:col-span-2 space-y-6" data-testid={`landing-tab-content-${activeSection}`}>
                    {/* Key on activeSection so React re-mounts + CSS fade-in
                        gives a seamless transition between tabs. */}
                    <div key={activeSection} className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {activeSection === "info" && (
                        <>
                            <InfoCard doc={doc} accent={accent} firstName={shortName} />
                            <ServicesCard doc={doc} accent={accent} />
                            <TrustAchievementsCard doc={doc} />
                            <EducationCard doc={doc} accent={accent} />
                        </>
                    )}

                    {/* iter100.28 — Simulator is always shown on the
                        public doctor page now (was previously gated by
                        `doc.show_simulator`). The `landing_show_simulator`
                        field is retained on the doctor record so an
                        admin can still hide it in edge-case compliance
                        scenarios, but the doctor no longer has a toggle
                        for it in their editor. */}
                    {activeSection === "simulate" && doc.show_simulator !== false && (
                        <SimulatorCard doc={doc} slug={slug} accent={accent} firstName={shortName} />
                    )}
                    {activeSection === "simulate" && doc.show_simulator === false && (
                        <TabPlaceholder
                            title="Smile Preview isn't enabled here"
                            body="This provider isn't offering the AI Smile Preview at the moment. Book an appointment for a personalised consultation."
                            accent={accent}
                        />
                    )}

                    {activeSection === "assessment" && (
                        <AssessmentTab doctorId={doc.doctor_id} accent={accent} />
                    )}

                    {activeSection === "articles" && (
                        <ArticlesSection items={doc.articles || []} accent={accent} doc={doc} slug={slug} />
                    )}

                    {activeSection === "case_studies" && (
                        <CaseStudiesSection items={doc.case_studies || []} accent={accent} doc={doc} slug={slug} />
                    )}

                    {activeSection === "testimonials" && (
                        <TestimonialsSection items={doc.testimonials || []} accent={accent} doctorName={doc.name} />
                    )}

                    {activeSection === "clinic" && (
                        <ClinicGalleryCard doc={doc} clinic={clinic} accent={accent} />
                    )}
                    </div>
                </main>

                {/* RIGHT column (sticky sidebar) */}
                <aside className="lg:col-span-1">
                    <div className="lg:sticky lg:top-24 space-y-4">
                        <BookingSidebar
                            doc={doc}
                            accent={accent}
                            clinic={clinic}
                            firstName={shortName}
                            onOpenBooking={() => setBookingOpen(true)}
                        />
                    </div>
                </aside>
            </div>

            {/* Footer trust strip */}
            <section className="border-t border-slate-200 bg-white mt-8">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                    <div className="text-xs text-slate-600 flex items-center gap-2">
                        <SealCheck size={16} weight="fill" style={{ color: accent.base }} />
                        <span><strong className="text-slate-900">{clinic.clinic_name || doc.name}</strong> · Certified Invisique™ Provider</span>
                    </div>
                    <Link to="/why-invisique" className="text-xs font-semibold hover:underline" style={{ color: accent.base }}>
                        Learn about Invisique™ <ArrowRight size={11} className="inline" />
                    </Link>
                </div>
            </section>

            {/* iter100.16 — Single booking modal shared by ALL "Book" CTAs
                (hero pill, sidebar button, Next Available chip). 2-step
                flow: pick slot → fill details + consent → OTP handoff. */}
            {bookingOpen && (
                <BookingFlowModal
                    doc={doc}
                    slug={slug}
                    accent={accent}
                    clinic={clinic}
                    onClose={() => setBookingOpen(false)}
                    onConfirm={(payload) => {
                        // Handoff to the existing OTP+booking flow. Keeps
                        // BookAppointment.jsx's session-persist logic intact
                        // by passing the same `b_*` params it already reads.
                        const dateStr = format(payload.date, "yyyy-MM-dd");
                        const draft = {
                            doctorId: doc.doctor_id,
                            date: dateStr,
                            time: payload.time,
                            notes: (payload.notes || "").trim(),
                            name: (payload.name || "").trim(),
                            email: (payload.email || "").trim(),
                            ts: Date.now(),
                        };
                        try {
                            window.sessionStorage.setItem("invisique_pending_booking", JSON.stringify(draft));
                        } catch { /* private mode — URL params still carry it across */ }

                        const qp = new URLSearchParams({
                            intent: "book",
                            b_doctor: doc.doctor_id,
                            b_date: dateStr,
                            b_time: payload.time,
                            b_name: draft.name,
                            b_phone: (payload.phone || "").trim(),
                            ref: "landing",
                        });
                        if (draft.notes) qp.set("b_notes", draft.notes);
                        if (draft.email) qp.set("b_email", draft.email);

                        const loginPath = `/patient-login?${qp.toString()}`;
                        const dest = portalUrl(loginPath);
                        api.post("/public/doctor-page/event", { kind: "lead", slug }).catch(() => {});
                        if (dest.startsWith("http")) {
                            window.location.assign(dest);
                        } else {
                            navigate(dest, { state: { from: `/book/${doc.doctor_id}` } });
                        }
                    }}
                />
            )}

            {/* iter100.166 — Sticky mobile CTA bar. Mobile-only (`sm:hidden`),
                appears once the identity card scrolls above the fold. Single
                primary "Book Appointment" action — highest converting on
                patient-facing dental pages (Practo, Zocdoc pattern). Uses the
                doctor's accent color for brand cohesion and respects iOS
                safe-area via env(safe-area-inset-bottom). */}
            <div
                aria-hidden={!showStickyCTA}
                data-testid="landing-sticky-mobile-cta"
                className={`sm:hidden fixed inset-x-0 bottom-0 z-40 transition-all duration-300 ease-out ${
                    showStickyCTA ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
                }`}
                style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
            >
                <div className="bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-6px_20px_-8px_rgba(15,23,42,0.15)] px-4 pt-2 pb-3">
                    {nextSlotLabel && (
                        <div
                            className="flex items-center justify-center gap-1.5 mb-1.5"
                            data-testid="landing-sticky-mobile-cta-availability"
                        >
                            {isAvailableToday ? (
                                <>
                                    <span
                                        className="relative flex h-2 w-2"
                                        aria-hidden="true"
                                    >
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                    </span>
                                    <span className="text-[11px] font-semibold text-emerald-700">
                                        Available today
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                                        Next
                                    </span>
                                    <span className="text-[11px] font-semibold text-slate-700">
                                        {nextSlotLabel}
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => setBookingOpen(true)}
                        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full text-white text-sm font-semibold shadow-sm active:scale-[0.99] transition-transform"
                        style={{ backgroundColor: accent.base }}
                        data-testid="landing-sticky-mobile-cta-book"
                    >
                        <CalendarBlank size={16} weight="fill" />
                        <span>Book Appointment</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =========================================================================
   IDENTITY BAND — Practo-style profile header. Photo + name + credentials
   in a single wide card that sits over a slim cover band. iter100.11 —
   integrates the 3 lead-gen quick actions (Smile Preview / Assessment /
   Callback) as compact colored pills at the bottom of the card, freeing
   up space for the sub-nav tabs below.
   ========================================================================= */
function IdentityBand({ doc, slug, accent, clinic, activeSection, onOpenSimulator, onOpenAssessment, onOpenBooking, cardRef }) {
    // iter100.58 — Pull next-available slot once; feed it into the primary
    // "Book Appointment" CTA subtitle. Replaces the old dedicated chip.
    const nextSlot = useNextAvailable(doc.doctor_id);
    const [callbackOpen, setCallbackOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    /* iter100.17 — Share URL is the *current* public landing page URL so
       whoever scans / copies gets an exact match of what the doctor is
       looking at. Falls back to window.location if slug isn't set. */
    const shareUrl = typeof window !== "undefined"
        ? `${window.location.origin}/dr/${slug}`
        : `/dr/${slug}`;
    return (
        <section className="relative">
            {/* Cover band (image if uploaded, else soft gradient using the accent) */}
            <div className="relative h-40 sm:h-48 overflow-hidden" data-testid="landing-cover-band">
                {doc.cover_image ? (
                    <>
                        <img src={doc.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover" />
                        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.15) 0%, rgba(15,23,42,0.5) 100%)" }} />
                    </>
                ) : (
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(135deg, ${accent.base} 0%, ${accent.text} 100%)`,
                        }}
                    >
                        {/* Subtle mesh pattern for texture, no gradients on gradients */}
                        <div className="absolute inset-0 opacity-[0.08]" style={{
                            backgroundImage: "radial-gradient(white 1.5px, transparent 1.5px)",
                            backgroundSize: "24px 24px",
                        }} />
                    </div>
                )}
            </div>

            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                {/* Overlapping identity card — hallmark Practo pattern */}
                <div ref={cardRef} className="-mt-24 sm:-mt-20 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7 relative" data-testid="landing-identity-card">
                    <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start">
                        {/* Photo */}
                        <div className="relative shrink-0">
                            {doc.image ? (
                                <img
                                    src={doc.image}
                                    alt={doc.name}
                                    className="h-36 w-36 sm:h-44 sm:w-44 rounded-2xl object-cover border-4 border-white shadow-md"
                                />
                            ) : (
                                <div className="h-36 w-36 sm:h-44 sm:w-44 rounded-2xl border-4 border-white shadow-md flex items-center justify-center font-display text-5xl font-bold text-white" style={{ backgroundColor: accent.base }}>
                                    {(doc.name || "?").charAt(0)}
                                </div>
                            )}
                            <div className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-white shadow flex items-center justify-center border border-slate-200" title="DCI-verified provider">
                                <SealCheck size={22} weight="fill" style={{ color: accent.base }} />
                            </div>
                        </div>

                        {/* Identity — now spans full width (no right column) */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-2 flex-wrap">
                                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight tracking-tight" data-testid="landing-hero-name">
                                    {doc.name}
                                </h1>
                                <span
                                    className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-bold uppercase tracking-wider mt-1.5"
                                    style={{ backgroundColor: accent.soft, color: accent.text }}
                                    title="Certified Invisique™ Provider"
                                >
                                    <Certificate size={11} weight="fill" /> Invisique™ Certified
                                </span>
                                {/* iter100.19 — Consult fee chip removed from the
                                    header (it still appears in the booking
                                    sidebar's "Consult Fee" block, so no info is
                                    lost). The Share Profile button now owns the
                                    top-right slot in the name row — a stronger,
                                    action-oriented use of that real estate.
                                    iter100.165 — On MOBILE (< sm) the flex-wrap
                                    parent used to push this button below the
                                    photo (line 4 of the identity card). Now
                                    absolutely-pinned to the top-right of the
                                    identity card on mobile so patients see it
                                    the moment they land — and re-slotted into
                                    the name-row flow on sm+ where wrapping
                                    isn't an issue. */}
                                <button
                                    type="button"
                                    onClick={() => setShareOpen(true)}
                                    className="absolute top-3 right-3 sm:static sm:top-auto sm:right-auto sm:ml-auto sm:mt-1 inline-flex items-center gap-1.5 h-7 px-3 rounded-full border bg-white hover:shadow-sm transition-all text-[12px] font-semibold shadow-sm sm:shadow-none z-10"
                                    style={{ borderColor: accent.ring, color: accent.text }}
                                    data-testid="landing-share-profile-btn"
                                    aria-label="Share this doctor's profile"
                                    title="Share profile"
                                >
                                    <ShareNetwork size={13} weight="fill" style={{ color: accent.base }} />
                                    Share profile
                                </button>
                            </div>

                            {doc.specialization && (
                                <div className="mt-1 text-sm sm:text-base font-medium text-slate-700 flex items-center gap-1.5">
                                    <Stethoscope size={15} weight="duotone" style={{ color: accent.base }} />
                                    {doc.specialization}
                                </div>
                            )}

                            {/* Credentials line — Practo-style comma-separated key facts */}
                            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-slate-600">
                                {doc.qualifications && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <GraduationCap size={14} weight="duotone" className="text-slate-500" />
                                        {doc.qualifications}
                                    </span>
                                )}
                                {doc.years_experience ? (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Clock size={14} weight="duotone" className="text-slate-500" />
                                        {doc.years_experience} years experience
                                    </span>
                                ) : null}
                                {doc.languages?.length > 0 && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <Translate size={14} weight="duotone" className="text-slate-500" />
                                        {doc.languages.slice(0, 3).map(_titleCase).join(", ")}
                                    </span>
                                )}
                            </div>

                            {clinic.clinic_name && (
                                <div className="mt-2 flex items-center gap-1.5 text-[13px] text-slate-600" data-testid="landing-clinic-name-row">
                                    <Buildings size={14} weight="duotone" className="text-slate-500" />
                                    <span className="font-medium text-slate-800">{clinic.clinic_name}</span>
                                    {clinic.city && <><span className="text-slate-400">·</span><MapPin size={13} weight="duotone" className="text-slate-500" /> {clinic.city}</>}
                                </div>
                            )}

                            {/* iter100.42 — Trust chips are now server-computed
                                from the doctor's chip_config (auto visibility +
                                admin badges + self-attested flags, max 8 chips).
                                Keeps the public page and editor preview in sync.
                                iter100.53 (2026-02-28) — Semantic per-source
                                colour palette: `auto` chips render teal (platform
                                signals), `admin` render rose (Invisique-curated
                                marketing badges), and `doctor` / `derived` flags
                                render green (self-attested practice attributes).
                                iter100.54 — Removed the patient-facing "How chips
                                work" link; that entry point now lives on the
                                marketing site footer and in the doctor's
                                portal, not on the microsite itself. */}
                            {(() => {
                                const chips = doc.chips || [];
                                // iter100.63 — Split trust chips into two rows
                                // by ownership: auto (platform-verified) and
                                // admin (Invisique-awarded). Each row gets its
                                // own eyebrow label so patients can read the
                                // hierarchy at a glance without decoding the
                                // colour palette.
                                const isTrustSource = (c) => c.source === "auto" || c.source === "admin";
                                const trustChips = chips.filter((c) => isTrustSource(c));
                                const verifiedChips = trustChips.filter((c) => c.source === "auto");
                                const awardedChips  = trustChips.filter((c) => c.source === "admin");
                                return (
                                    <>
                                        {verifiedChips.length > 0 && (
                                            <div className="mt-4">
                                                <div
                                                    className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5"
                                                    data-testid="landing-verified-chips-label"
                                                >
                                                    Verified Credentials
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2" data-testid="landing-verified-chips">
                                                    {verifiedChips.map((c) => (
                                                        <TrustChip
                                                            key={c.key}
                                                            label={c.label}
                                                            icon={<PublicChipIcon name={c.icon} size={13} />}
                                                            tier={1}
                                                            source={c.source}
                                                            explanation={c.explanation}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {awardedChips.length > 0 && (
                                            <div className="mt-3">
                                                <div
                                                    className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5"
                                                    data-testid="landing-awarded-chips-label"
                                                >
                                                    Invisique&trade; Awards
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2" data-testid="landing-awarded-chips">
                                                    {awardedChips.map((c) => (
                                                        <TrustChip
                                                            key={c.key}
                                                            label={c.label}
                                                            icon={<PublicChipIcon name={c.icon} size={13} />}
                                                            tier={1}
                                                            source={c.source}
                                                            explanation={c.explanation}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>

                    {/* iter100.63 — In-Clinic Facilities row breaks out of the
                        photo+identity flex container so it starts at the left
                        edge of the identity card (aligned with the doctor's
                        photo). Uses the same doc.chips payload but re-filters
                        to only the practice-attribute source buckets. */}
                    {(() => {
                        const attrs = (doc.chips || []).filter(
                            (c) => (c.tier || 2) === 2 && c.source !== "auto" && c.source !== "admin",
                        );
                        if (attrs.length === 0) return null;
                        return (
                            <div className="mt-4">
                                <div
                                    className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5"
                                    data-testid="landing-practice-chips-label"
                                >
                                    In-Clinic Facilities
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5" data-testid="landing-practice-chips">
                                    {attrs.map((c) => (
                                        <TrustChip
                                            key={c.key}
                                            label={c.label}
                                            icon={<PublicChipIcon name={c.icon} size={11} />}
                                            tier={2}
                                            source={c.source}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* iter100.14 — CTA pills row now spans the full width of the
                        identity card (photo excluded). 4 equally-sized pills:
                        Book Appointment (primary teal), Smile Preview (purple),
                        Smile Assessment (blue), Request Callback (green).
                        iter100.63 — Divider bumped from slate-100 to slate-150-ish
                        to visually separate the CTA row from the In-Clinic
                        Facilities row that now sits directly above it. */}
                    <div className="mt-5 pt-5 border-t border-slate-200/80 flex flex-wrap gap-2" data-testid="landing-header-cta-row">
                        <QuickActionPill
                            icon={<CalendarBlank size={13} weight="fill" />}
                            label="Book Appointment"
                            subtle={nextSlot ? `Next: ${nextSlot}` : "Choose a slot"}
                            colorScheme={PILL_COLORS.book}
                            onClick={onOpenBooking}
                            testId="landing-cta-book"
                            fullWidth
                        />
                        {doc.show_simulator !== false && (
                            <QuickActionPill
                                icon={<Sparkle size={13} weight="fill" />}
                                label="Try Smile Preview"
                                subtle="AI · 30s"
                                colorScheme={PILL_COLORS.simulator}
                                onClick={onOpenSimulator}
                                active={activeSection === "simulate"}
                                testId="landing-cta-simulator"
                                fullWidth
                            />
                        )}
                        <QuickActionPill
                            icon={<ClipboardText size={13} weight="fill" />}
                            label="Free Smile Assessment"
                            subtle="~2 min quiz"
                            colorScheme={PILL_COLORS.assessment}
                            onClick={() => {
                                api.post("/public/doctor-page/event", { kind: "lead", slug }).catch(() => {});
                                onOpenAssessment();
                            }}
                            active={activeSection === "assessment"}
                            testId="landing-cta-assessment"
                            fullWidth
                        />
                        <QuickActionPill
                            icon={<PhoneCall size={13} weight="fill" />}
                            label="Request a Callback"
                            subtle="Call in 30 min"
                            colorScheme={PILL_COLORS.callback}
                            onClick={() => setCallbackOpen(true)}
                            testId="landing-cta-callback"
                            fullWidth
                        />
                    </div>
                </div>
            </div>

            {callbackOpen && (
                <CallbackModal
                    doc={doc}
                    slug={slug}
                    accent={accent}
                    onClose={() => setCallbackOpen(false)}
                />
            )}

            <ShareProfileModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                url={shareUrl}
                title={doc.name}
                subtitle={clinic.clinic_name ? `${clinic.clinic_name}${clinic.city ? " · " + clinic.city : ""}` : "Certified Invisique™ Provider"}
                accent={accent}
                shareMessage={`Meet ${doc.name} — a Certified Invisique™ Provider${clinic.city ? ` in ${clinic.city}` : ""}. Book a consultation or preview your smile with AI: ${shareUrl}`}
            />
        </section>
    );
}

/* iter100.11 — Distinct color themes for each lead-gen path so patients
   recognise "the purple wand is Smile Preview" instinctively. All 4 stay
   readable in both light/dark surfaces. Book (primary) uses the doctor's
   accent color; others use their unique hue. */
const PILL_COLORS = {
    book:       { bg: "hsl(192 88% 96%)", border: "hsl(192 88% 82%)", text: "hsl(192 90% 25%)", iconBg: "hsl(192 88% 33%)", activeBg: "hsl(192 90% 25%)" },
    simulator:  { bg: "hsl(280 85% 96%)", border: "hsl(280 85% 88%)", text: "hsl(280 70% 40%)", iconBg: "hsl(280 85% 60%)", activeBg: "hsl(280 70% 45%)" },
    assessment: { bg: "hsl(215 90% 96%)", border: "hsl(215 90% 88%)", text: "hsl(215 70% 40%)", iconBg: "hsl(215 90% 55%)", activeBg: "hsl(215 70% 45%)" },
    callback:   { bg: "hsl(142 65% 95%)", border: "hsl(142 65% 85%)", text: "hsl(142 60% 30%)", iconBg: "hsl(142 65% 45%)", activeBg: "hsl(142 60% 35%)" },
};

function QuickActionPill({ icon, label, subtle, colorScheme, onClick, active, testId, fullWidth }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`group inline-flex items-center gap-2 h-11 pl-1.5 pr-3.5 rounded-full border transition-all hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 ${fullWidth ? "flex-1 basis-52 min-w-[180px] justify-start" : ""}`}
            style={active
                ? { backgroundColor: colorScheme.activeBg, borderColor: colorScheme.activeBg, color: "white" }
                : { backgroundColor: colorScheme.bg, borderColor: colorScheme.border, color: colorScheme.text }}
            data-testid={testId}
        >
            <span
                className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: active ? "rgba(255,255,255,0.2)" : colorScheme.iconBg, color: "white" }}
            >
                {icon}
            </span>
            <span className="flex flex-col items-start leading-tight text-left">
                <span className="text-[13px] font-semibold whitespace-nowrap">{label}</span>
                <span className="text-[10px] opacity-80 whitespace-nowrap">{subtle}</span>
            </span>
        </button>
    );
}

/* iter100.58 — Hook (was: NextAvailableChip component). Fetches the
   doctor's next open consultation slot from /availability-summary and
   returns a pre-formatted string like "Sat 12 Jul · 9:00 AM" ready to
   drop into a CTA subtitle. Returns null while loading / on error. */
function useNextAvailable(doctorId) {
    const [next, setNext] = useState(null);
    useEffect(() => {
        if (!doctorId) return;
        let cancelled = false;
        api.get(`/doctors/${doctorId}/availability-summary`, { params: { days: 14 } })
            .then((r) => {
                if (cancelled) return;
                const first = Array.isArray(r.data) ? r.data.find((s) => s.has_slots) : null;
                if (first) setNext({ date: first.date, time: first.first_slot });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [doctorId]);
    if (!next) return null;
    const [h, m] = next.time.split(":").map(Number);
    const timeStr = `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
    return `${format(parseISO(next.date), "EEE d MMM")} · ${timeStr}`;
}

/* iter100.53 — Semantic chip palettes by source bucket.
   auto  → Teal   (platform-computed signals: DCI Verified, Cases treated, etc.)
   admin → Rose   (Invisique-curated marketing badges)
   doctor / derived → Green  (self-attested practice attributes)
   The doctor's own microsite accent no longer paints chips — bucket colour
   is deliberately consistent across every doctor so patients can learn to
   read the language once, then scan any profile at a glance. */
const CHIP_PALETTES = {
    auto:    { bg: "hsl(192 90% 96%)", border: "hsl(192 90% 82%)", text: "hsl(192 90% 25%)", softBg: "hsl(192 55% 97%)", softBorder: "hsl(192 40% 90%)", softText: "hsl(192 55% 30%)" },
    admin:   { bg: "hsl(340 78% 97%)", border: "hsl(340 78% 88%)", text: "hsl(340 65% 38%)", softBg: "hsl(340 60% 98%)", softBorder: "hsl(340 40% 92%)", softText: "hsl(340 45% 42%)" },
    doctor:  { bg: "hsl(150 55% 96%)", border: "hsl(150 45% 82%)", text: "hsl(150 60% 28%)", softBg: "hsl(150 40% 97%)", softBorder: "hsl(150 30% 90%)", softText: "hsl(150 40% 32%)" },
    derived: { bg: "hsl(150 55% 96%)", border: "hsl(150 45% 82%)", text: "hsl(150 60% 28%)", softBg: "hsl(150 40% 97%)", softBorder: "hsl(150 30% 90%)", softText: "hsl(150 40% 32%)" },
};

/* Trust chip pill. iter100.53 — Tier + source aware:
   Tier 1 — bold pill, source-palette background, tooltip on hover.
   Tier 2 — compact pill, same source-palette (softer variant), no tooltip. */
function TrustChip({ label, icon, tier = 1, source = "auto", explanation }) {
    const p = CHIP_PALETTES[source] || CHIP_PALETTES.auto;
    if (tier === 2) {
        return (
            <span
                className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10.5px] font-medium border"
                style={{ backgroundColor: p.softBg, borderColor: p.softBorder, color: p.softText }}
                data-testid={`landing-chip-tier2-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {icon}{label}
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[12px] font-semibold border shadow-sm cursor-help"
            style={{ backgroundColor: p.bg, borderColor: p.border, color: p.text }}
            title={explanation || undefined}
            data-testid={`landing-chip-tier1-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            {icon}{label}
        </span>
    );
}

/* iter100.42 — Icon-name → Phosphor component map for server-emitted chips.
   Matches the map used in LandingPageEditor.jsx. Unknown keys fall back
   to CheckCircle so we never crash on a taxonomy addition.
   iter100.57 — Extended with all badge-catalog icons so the new Trust &
   Achievements card can render every one of the 24 catalog keys. */
const PUBLIC_CHIP_ICON_MAP = {
    Certificate, ShieldCheck, CheckCircle, Sparkle, Calendar,
    Star, UsersFour, Trophy, SealCheck, Medal,
    Baby, Lightning, Wheelchair, GenderFemale, FirstAid, House, CreditCard, Rainbow,
    CalendarBlank, ScanSmiley, Buildings, Translate,
    Diamond, Heart, ChatCircle, Stethoscope, ArrowsClockwise, GraduationCap, BookOpen,
    MapPin, Rocket, TestTube,
    // iter100.63 — Doctor practice-attribute icons (Moon, Coffee already
    // imported at top; add the missing five so the Clinic tab tiles
    // render meaningful icons instead of fallback CheckCircles).
    Moon, Coffee, Car, VideoCamera, Toilet, WifiHigh,
    // iter100.204 — Growth Partner badge (referrer recognition).
    Handshake,
};
function PublicChipIcon({ name, size = 12 }) {
    const C = PUBLIC_CHIP_ICON_MAP[name] || CheckCircle;
    return <C size={size} weight="fill" />;
}

/* =========================================================================
   TRUST & ACHIEVEMENTS — iter100.57
   Public "Trust & Achievements" section grouped by badge category.
   Renders the union of admin-granted + auto-derived badges from the chip
   payload (source==='admin'). Uses per-category tints; the identity strip
   above intentionally keeps its rose = admin convention so the two
   surfaces don't step on each other visually. Empty state → hide.
   ========================================================================= */
/* iter100.65 — "Heritage Collection" medal palette. Replaces the earlier
   bright jewel tones with muted, antique metal + gemstone hues that read
   as a curated award case rather than a rainbow of achievement badges.
   The whole palette is deliberately low-saturation, low-luminance — like
   the medals you'd see on a hospital honor board or a professor's office
   wall, not a video-game achievements panel.

   Isolated to Trust & Achievements only. Header identity chips
   (auto=teal / admin=rose / practice=green) are untouched. */
const CATEGORY_META = {
    milestone:   { label: "Milestone",   palette: { bg: "hsl(38 55% 96%)",  border: "hsl(38 40% 82%)",  text: "hsl(38 45% 30%)",  accent: "hsl(38 55% 50%)"  } },  // Antique champagne gold
    clinical:    { label: "Clinical",    palette: { bg: "hsl(218 55% 97%)", border: "hsl(218 40% 85%)", text: "hsl(218 50% 26%)", accent: "hsl(218 55% 32%)" } },  // Midnight sapphire
    performance: { label: "Performance", palette: { bg: "hsl(12 60% 96%)",  border: "hsl(12 40% 84%)",  text: "hsl(12 55% 30%)",  accent: "hsl(12 60% 42%)"  } },  // Cinnabar lacquer
    recognition: { label: "Recognition", palette: { bg: "hsl(295 40% 97%)", border: "hsl(295 30% 86%)", text: "hsl(295 35% 30%)", accent: "hsl(295 38% 34%)" } },  // Royal aubergine
    volume:      { label: "Volume",      palette: { bg: "hsl(28 45% 96%)",  border: "hsl(28 35% 82%)",  text: "hsl(28 45% 26%)",  accent: "hsl(28 45% 40%)"  } },  // Antique bronze
    community:   { label: "Community",   palette: { bg: "hsl(155 40% 96%)", border: "hsl(155 30% 82%)", text: "hsl(155 40% 22%)", accent: "hsl(155 40% 28%)" } },  // Malachite
};
const CATEGORY_ORDER = ["milestone", "clinical", "performance", "recognition", "volume", "community"];
const TRUST_MAX_VISIBLE = 8;

function TrustAchievementsCard({ doc }) {
    // Pull the admin-source chips out of the payload; keep original ordering.
    const admin = (doc.chips || []).filter((c) => c.source === "admin");
    if (admin.length === 0) return null;
    const visible = admin.slice(0, TRUST_MAX_VISIBLE);

    // Group by category, preserving CATEGORY_ORDER.
    const grouped = new Map();
    for (const b of visible) {
        const cat = b.category || "recognition";
        if (!grouped.has(cat)) grouped.set(cat, []);
        grouped.get(cat).push(b);
    }
    const groups = CATEGORY_ORDER
        .map((k) => ({ key: k, items: grouped.get(k) || [] }))
        .filter((g) => g.items.length > 0);

    return (
        <section
            className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7"
            data-testid="landing-trust-achievements"
        >
            <div className="flex items-center gap-2 mb-4">
                <SectionIcon><Medal size={16} weight="duotone" className="text-slate-700" /></SectionIcon>
                <h2 className="font-display text-lg font-bold text-slate-900">Trust &amp; Achievements</h2>
                <span className="ml-1 text-[11px] text-slate-500 font-semibold">· {visible.length}</span>
                {admin.length > TRUST_MAX_VISIBLE && (
                    <span className="ml-auto text-[11px] text-slate-500">
                        Showing top {TRUST_MAX_VISIBLE} of {admin.length}
                    </span>
                )}
            </div>
            <div className="space-y-4">
                {groups.map(({ key, items }) => {
                    const meta = CATEGORY_META[key];
                    return (
                        <div key={key}>
                            <div
                                className="text-[10.5px] font-bold uppercase tracking-[0.16em] mb-2"
                                style={{ color: meta.palette.text }}
                            >
                                {meta.label}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {items.map((b) => (
                                    <TrustBadgeCard key={b.key} badge={b} palette={meta.palette} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function TrustBadgeCard({ badge, palette }) {
    const isAuto = badge.auto_derived;
    // iter100.63 — Medallion redesign. Every award now renders as a
    // proper metal medal: circular gradient face + edge highlight +
    // inset shadow for depth, small ribbon tab at the bottom, and (for
    // admin-awarded, not auto) a scatter of tiny stars orbiting the top
    // corner for that "just polished" shine. `color-mix()` handles the
    // per-category tint variations so a single palette drives all the
    // depth cues — no new palette keys needed.
    const accent = palette.accent;
    // Metal-face gradient stops. Highlight → base → shade → deep shade.
    const gradient = `radial-gradient(circle at 32% 28%,
        color-mix(in srgb, ${accent} 20%, white) 0%,
        color-mix(in srgb, ${accent} 65%, white) 30%,
        ${accent} 62%,
        color-mix(in srgb, ${accent} 78%, black) 100%)`;
    // Elevation: colored glow + inset shine on top edge + inset shade on bottom.
    const medalShadow = [
        `0 6px 14px -4px color-mix(in srgb, ${accent} 55%, transparent)`,
        `0 2px 4px -1px rgba(0,0,0,0.18)`,
        `inset 0 2px 3px rgba(255,255,255,0.55)`,
        `inset 0 -3px 5px color-mix(in srgb, ${accent} 60%, black 30%)`,
    ].join(", ");
    // The "ribbon" — a tiny V-shaped tab under the medal, painted in the
    // deeper shade so it reads as a folded cloth strip.
    const ribbon = `color-mix(in srgb, ${accent} 80%, black)`;

    return (
        <div
            className="relative group flex items-start gap-3.5 rounded-xl border px-4 py-3.5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            style={{
                borderColor: palette.border,
                background: `linear-gradient(135deg, white 0%, white 55%, ${palette.bg} 100%)`,
            }}
            data-testid={`landing-trust-badge-${badge.key}`}
            title={badge.explanation || undefined}
        >
            {/* ==================== MEDAL ==================== */}
            <div className="relative shrink-0 pt-0.5">
                {/* Ribbon (behind medal, peeks out below) — 2 stacked triangles */}
                <div className="absolute left-1/2 -translate-x-1/2 top-11 z-0" aria-hidden="true">
                    <svg width="34" height="18" viewBox="0 0 34 18" fill="none">
                        <path d="M4 0 L17 12 L30 0 L34 4 L20 18 L17 15 L14 18 L0 4 Z" fill={ribbon} opacity="0.85" />
                    </svg>
                </div>

                {/* Metal face */}
                <div
                    className="relative z-10 h-14 w-14 rounded-full overflow-hidden flex items-center justify-center text-white transition-transform duration-200 group-hover:scale-[1.03]"
                    style={{
                        background: gradient,
                        boxShadow: medalShadow,
                    }}
                >
                    {/* iter100.68 — Spotlight sweep. Sits above the metal
                        gradient but below the icon well so the "light" reads
                        as reflecting off the whole medal. Only animates on
                        hover of the parent card (`group:hover`).
                        `overflow-hidden` on this div clips the sweep to the
                        circular medal shape — the star cluster below is
                        deliberately rendered OUTSIDE this div so it can
                        overflow freely. */}
                    <span className="medal-shine" aria-hidden="true" />

                    {/* Icon inside a slight inset well for extra depth */}
                    <div
                        className="h-11 w-11 rounded-full flex items-center justify-center"
                        style={{
                            background: `radial-gradient(circle at 50% 40%, color-mix(in srgb, ${accent} 55%, white) 0%, ${accent} 70%)`,
                            boxShadow: `inset 0 1px 2px rgba(255,255,255,0.45), inset 0 -1px 2px rgba(0,0,0,0.2)`,
                        }}
                    >
                        <PublicChipIcon name={badge.icon} size={22} />
                    </div>
                </div>

                {/* Star cluster — only on admin-awarded (auto-derived don't
                    get the "shine" halo since they're not personally granted).
                    iter100.66 — Recoloured from yellow to the theme rose so
                    the stars visually tie back to Invisique's admin/awarded
                    semantic (rose = "granted by Invisique HQ"). Graduated
                    lightness across the 3 stars keeps the twinkle depth.
                    iter100.67 — Added the `trust-star-twinkle` animation
                    with staggered `animation-delay` values so the 3 stars
                    pulse in sequence instead of in unison. Respects
                    prefers-reduced-motion via a media query in index.css.
                    iter100.68 — Moved OUT of the medal-face div (which now
                    uses `overflow-hidden` for the shine sweep) so stars can
                    freely poke beyond the medal boundary as before. Layered
                    above via z-20 so the sweep never obscures them.
                    Positions match the previous placement — top-right cluster,
                    same offsets. */}
                {!isAuto && (
                    <div className="absolute inset-0 z-20 pointer-events-none">
                        <Star
                            className="absolute -top-0.5 -right-0.5 text-[hsl(340_82%_58%)] drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] trust-star-twinkle"
                            style={{ animationDelay: "0s" }}
                            size={12}
                            weight="fill"
                            data-testid={`landing-trust-star-${badge.key}`}
                        />
                        <Star
                            className="absolute top-1.5 -right-2 text-[hsl(340_75%_65%)] drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)] trust-star-twinkle"
                            style={{ animationDelay: "1s" }}
                            size={8}
                            weight="fill"
                        />
                        <Star
                            className="absolute -top-1.5 right-2.5 text-[hsl(340_70%_70%)] drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)] trust-star-twinkle"
                            style={{ animationDelay: "2s" }}
                            size={7}
                            weight="fill"
                        />
                    </div>
                )}
            </div>

            {/* ==================== CONTENT ==================== */}
            <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold text-slate-900 flex items-center gap-2 flex-wrap leading-tight">
                    {badge.label}
                    {isAuto ? (
                        <span
                            className="text-[9px] font-bold uppercase tracking-wider bg-white border px-1.5 py-0.5 rounded"
                            style={{ borderColor: palette.border, color: palette.text }}
                            title="Auto-awarded from live Invisique™ data"
                        >
                            Auto
                        </span>
                    ) : (
                        // iter100.63 — Awarded pill upgraded to feel like a
                        // pressed foil sticker: gradient + subtle shadow.
                        <span
                            className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-[0.08em] px-1.5 py-0.5 rounded text-white shadow-sm"
                            style={{
                                background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 90%, white) 0%, ${accent} 100%)`,
                                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.35), 0 1px 2px color-mix(in srgb, ${accent} 40%, transparent)`,
                            }}
                            title="Personally awarded by the Invisique™ team"
                        >
                            <Sparkle size={8} weight="fill" /> Awarded
                        </span>
                    )}
                </div>
                {badge.explanation && (
                    <div className="text-[11.5px] text-slate-600 leading-relaxed mt-1 line-clamp-2">
                        {badge.explanation}
                    </div>
                )}
            </div>
        </div>
    );
}


/* =========================================================================
   STICKY SUB-NAV — Practo-style horizontal tabs, always visible after the
   hero. Anchors to the section IDs of the content below.
   ========================================================================= */
function StickySubNav({ accent, activeSection, onSelect }) {
    return (
        <div className="sticky top-16 z-30 bg-white border-b-2 border-slate-100 shadow-sm" data-testid="landing-sub-nav">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className="flex items-center gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar h-14">
                    {SECTIONS.map((s) => {
                        const active = activeSection === s.id;
                        const Icon = s.icon;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => onSelect(s.id)}
                                className="shrink-0 h-14 px-3 sm:px-4 text-[14px] font-semibold relative transition-all flex items-center gap-1.5 group"
                                style={{ color: active ? accent.base : "hsl(215 20% 40%)" }}
                                data-testid={`landing-tab-${s.id}`}
                                aria-pressed={active}
                            >
                                <Icon
                                    size={16}
                                    weight={active ? "fill" : "duotone"}
                                    className="transition-transform group-hover:scale-110"
                                />
                                <span>{s.label}</span>
                                {/* Active underline */}
                                <span
                                    className="absolute left-2 right-2 bottom-0 h-[3px] rounded-t transition-all duration-300"
                                    style={{
                                        backgroundColor: active ? accent.base : "transparent",
                                        transform: active ? "scaleX(1)" : "scaleX(0)",
                                    }}
                                />
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function scrollToId(id) {
    const el = document.getElementById(id);
    if (!el) return;
    // Offset for sticky top nav (~64px) + sub-nav (~48px)
    const y = el.getBoundingClientRect().top + window.scrollY - 120;
    window.scrollTo({ top: y, behavior: "smooth" });
}

/* Tiny placeholder used when a tab has nothing to show (e.g. Simulator
   disabled for this doctor). Keeps the tabbed layout consistent. */
function TabPlaceholder({ title, body, accent }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="landing-tab-placeholder">
            <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: accent.soft }}>
                <Sparkle size={22} weight="duotone" style={{ color: accent.base }} />
            </div>
            <div className="font-display text-lg font-bold text-slate-900">{title}</div>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto leading-relaxed">{body}</p>
        </div>
    );
}

/* =========================================================================
   INFO — Practo's "About" section. Story + a small stats row.
   ========================================================================= */
function InfoCard({ doc, accent, firstName }) {
    return (
        <section id="info" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7" data-testid="landing-info-card">
            <div className="flex items-center gap-2 mb-3">
                <SectionIcon><User size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                <h2 className="font-display text-lg font-bold text-slate-900">About {firstName}</h2>
            </div>
            {doc.story ? (
                <p className="text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{doc.story}</p>
            ) : doc.bio ? (
                <p className="text-[15px] leading-relaxed text-slate-700 whitespace-pre-wrap">{doc.bio}</p>
            ) : (
                <p className="text-sm text-slate-500 italic">This provider hasn&apos;t added an introduction yet.</p>
            )}

            {doc.tagline && (
                <div
                    className="mt-5 pl-4 py-2 border-l-2 text-[14px] italic text-slate-700"
                    style={{ borderColor: accent.base }}
                >
                    &ldquo;{doc.tagline}&rdquo;
                </div>
            )}

            {/* Stats row */}
            <div className="mt-6 pt-5 border-t border-slate-100 grid grid-cols-3 gap-4">
                <Stat label="Experience" value={doc.years_experience ? `${doc.years_experience} yrs` : "—"} accent={accent} />
                <Stat label="Invisique™ Cases" value={doc.cases_treated ? `${doc.cases_treated}+` : "0"} accent={accent} />
                <Stat label="Languages" value={doc.languages?.length ? doc.languages.length : "—"} accent={accent} />
            </div>
        </section>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div>
            <div className="text-2xl font-bold" style={{ color: accent.base }}>{value}</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{label}</div>
        </div>
    );
}

function SectionIcon({ children }) {
    return <div className="h-7 w-7 rounded-lg bg-slate-100 flex items-center justify-center">{children}</div>;
}

/* =========================================================================
   SERVICES OFFERED — iter100.55 (2026-02-28).
   Replaced the old hardcoded "Specializations" grid (which contained fake
   marketing filler including a competitor brand name). The new card is
   doctor-curated: the doctor picks from a fixed taxonomy in the Landing
   editor, and this card renders ONLY what they picked. Empty pick → the
   card hides itself entirely.
   ========================================================================= */
function ServicesCard({ doc, accent }) {
    const items = doc.services || [];
    if (items.length === 0) return null;
    return (
        <section className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7" data-testid="landing-services-card">
            <div className="flex items-center gap-2 mb-4">
                <SectionIcon><Sparkle size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                <h2 className="font-display text-lg font-bold text-slate-900">Services offered</h2>
                <span className="ml-1 text-[11px] text-slate-500 font-semibold">· {items.length}</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {items.map((s) => (
                    <span
                        key={s.key}
                        className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-[13px] font-medium border"
                        style={{ backgroundColor: accent.soft, borderColor: accent.ring, color: accent.text }}
                        data-testid={`landing-service-${s.key}`}
                    >
                        <CheckCircle size={12} weight="fill" />{s.label}
                    </span>
                ))}
            </div>
        </section>
    );
}

/* =========================================================================
   AI SMILE SIMULATOR — Invisique's differentiator on a directory-shaped page.
   Rendered as a premium card that opens the SmileSimulator component inline.
   iter100.23 — Removed the "Start Smile Preview" gate button. The tab
   already IS the "Start" intent (patient just clicked the Smile Preview
   pill), so making them click again to reveal the upload widget was a
   redundant tap. The intro paragraph + tagline stay for context, and the
   upload UI now renders directly below.
   ========================================================================= */
function SimulatorCard({ doc, slug, accent, firstName }) {
    return (
        <section id="simulate" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 relative overflow-hidden" data-testid="landing-simulator-card">
            {/* Subtle accent wash */}
            <div className="absolute inset-0 pointer-events-none opacity-60" style={{ background: `linear-gradient(135deg, ${accent.soft} 0%, transparent 70%)` }} />
            <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                    <SectionIcon><Sparkle size={16} weight="fill" style={{ color: accent.base }} /></SectionIcon>
                    <h2 className="font-display text-lg font-bold text-slate-900">Try before you book</h2>
                    <span
                        className="ml-auto text-[10px] font-bold uppercase tracking-wider h-5 px-2 rounded-md inline-flex items-center gap-1"
                        style={{ backgroundColor: accent.base, color: "white" }}
                    >
                        <Sparkle size={10} weight="fill" />AI Preview
                    </span>
                </div>
                <p className="text-[13px] text-slate-600 leading-relaxed max-w-xl">
                    Upload a selfie and see a preview of your smile after clear-aligner treatment — curated for you by {firstName}. Free, private, no signup. Takes ~30 seconds.
                </p>
                <div className="mt-4">
                    <SmileSimulator
                        attributedDoctorId={doc.doctor_id}
                        onSimulate={() => {
                            api.post("/public/doctor-page/event", { kind: "simulate", slug }).catch(() => {});
                        }}
                    />
                </div>
            </div>
        </section>
    );
}

/* =========================================================================
   EDUCATION + REGISTRATIONS — Practo's "Education" section.
   iter100.319 — Replaced the static "Invisique™ Certified Provider" row
   with a dynamic credentials block sourced from `doc.credentials`. Auto-
   badges (Invisique Certified + DCI Verified) still show at the top of the
   Registrations rows; doctor-authored certs / awards / publications get
   their own second card with a subtle "self-declared" label.
   ========================================================================= */
function EducationCard({ doc, accent }) {
    const rows = [];
    if (doc.qualifications) rows.push({ icon: <GraduationCap size={14} weight="duotone" />, label: "Education", value: doc.qualifications });
    if (doc.years_experience) rows.push({ icon: <Clock size={14} weight="duotone" />, label: "Practice", value: `${doc.years_experience} years of clinical experience` });
    if (doc.languages?.length) rows.push({ icon: <Translate size={14} weight="duotone" />, label: "Speaks", value: doc.languages.map(_titleCase).join(", ") });
    // iter100.313 — Show the doctor's real DCI/state-council registration
    // number instead of a static "(Verified)" placeholder. Falls back to
    // the plain "Dental Council of India (Verified)" line only if the
    // doctor hasn't filled a reg number yet.
    const regNumber = (doc.dci_registration_number || "").trim();
    const stateCouncil = (doc.state_council || "").trim();
    if (regNumber) {
        const councilLabel = stateCouncil || "Dental Council of India";
        rows.push({
            icon: <ShieldCheck size={14} weight="duotone" />,
            label: "Registration",
            value: `${councilLabel} · Reg no. ${regNumber} (Verified)`,
        });
    } else {
        rows.push({ icon: <ShieldCheck size={14} weight="duotone" />, label: "Registration", value: "Dental Council of India (Verified)" });
    }
    // Invisique platform certification is an auto-badge (verified server-
    // side by lifecycle_status = "published").
    rows.push({ icon: <Certificate size={14} weight="duotone" />, label: "Certification", value: "Invisique™ Certified Provider" });

    const credentials = Array.isArray(doc.credentials) ? doc.credentials : [];

    return (
        <>
            <section className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7" data-testid="landing-education-card">
                <div className="flex items-center gap-2 mb-4">
                    <SectionIcon><GraduationCap size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                    <h2 className="font-display text-lg font-bold text-slate-900">Education & Registrations</h2>
                </div>
                <ul className="divide-y divide-slate-100">
                    {rows.map((r, i) => (
                        <li key={i} className="py-3 flex items-start gap-3">
                            <span className="mt-0.5 shrink-0" style={{ color: accent.base }}>{r.icon}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{r.label}</div>
                                <div className="text-[14px] text-slate-800 mt-0.5">{r.value}</div>
                            </div>
                        </li>
                    ))}
                </ul>
            </section>
            {credentials.length > 0 && <CredentialsCard credentials={credentials} accent={accent} />}
        </>
    );
}


/* =========================================================================
   CREDENTIALS CARD — doctor-authored certifications, awards, publications.
   Shown as a second card below Education & Registrations. Sorted by year
   desc. All entries carry a subtle "Self-declared" label so patients
   understand these are doctor claims (Invisique doesn't verify them in v1).
   ========================================================================= */
function CredentialsCard({ credentials, accent }) {
    const kindConfig = {
        certification: { label: "Certification", icon: <Certificate size={14} weight="duotone" /> },
        award:         { label: "Award",         icon: <Trophy size={14} weight="duotone" /> },
        publication:   { label: "Publication",   icon: <BookOpen size={14} weight="duotone" /> },
    };
    const sorted = [...credentials].sort((a, b) => (b.year || 0) - (a.year || 0));

    return (
        <section
            className="mt-4 bg-white rounded-2xl border border-slate-200 p-6 sm:p-7"
            data-testid="landing-credentials-card"
        >
            <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                    <SectionIcon><Certificate size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                    <h2 className="font-display text-lg font-bold text-slate-900">Certifications & Achievements</h2>
                </div>
                <span className="hidden sm:inline text-[10.5px] font-bold uppercase tracking-widest text-slate-400">
                    Self-declared
                </span>
            </div>
            <ul className="divide-y divide-slate-100">
                {sorted.map((c) => {
                    const cfg = kindConfig[c.kind] || kindConfig.certification;
                    return (
                        <li key={c.id} className="py-3 flex items-start gap-3" data-testid={`landing-credential-${c.id}`}>
                            <span className="mt-0.5 shrink-0" style={{ color: accent.base }}>{cfg.icon}</span>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                    <span className="text-[14px] font-semibold text-slate-800">{c.title}</span>
                                    {c.year && (
                                        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{c.year}</span>
                                    )}
                                    <span className="rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-widest">
                                        {cfg.label}
                                    </span>
                                </div>
                                {c.issuer && (
                                    <div className="text-[12.5px] text-slate-500 mt-0.5">{c.issuer}</div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
            <p className="mt-3 text-[11px] text-slate-400 sm:hidden">Self-declared by the provider.</p>
        </section>
    );
}

/* =========================================================================
   EmptyState — shared empty-content placeholder used by Articles / Case
   Studies / Testimonials / Clinic photos when a doctor hasn't populated
   that section yet.
   ========================================================================= */
function EmptyState({ icon, text, accent }) {
    return (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 px-6 text-center" data-testid="landing-empty-state">
            <div className="mx-auto h-10 w-10 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: accent.soft }}>
                {icon}
            </div>
            <div className="text-[13px] text-slate-500 max-w-xs mx-auto leading-snug">{text}</div>
        </div>
    );
}

/* =========================================================================
   ARTICLES — the doctor's writings. Each row: cover thumbnail, title,
   summary, optional external link. Anchors the "Articles" sub-nav tab.
   ========================================================================= */
function ArticlesSection({ items, accent, doc, slug }) {
    const drName = shortDoctorName(doc?.name) || "This provider";
    return (
        <section id="articles" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7" data-testid="landing-articles-card">
            <div className="flex items-center gap-2 mb-4">
                <SectionIcon><Newspaper size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                <h2 className="font-display text-lg font-bold text-slate-900">Articles</h2>
                {items.length > 0 && (
                    <span className="ml-1 text-[11px] text-slate-500 font-semibold">· {items.length}</span>
                )}
            </div>
            {items.length === 0 ? (
                <EmptyState
                    icon={<Article size={22} weight="duotone" style={{ color: accent.base }} />}
                    text={`${drName} hasn\u2019t published any articles yet. Check back soon.`}
                    accent={accent}
                />
            ) : (
                <div className="space-y-4">
                    {items.map((row) => {
                        /* iter100.171 — Fall back to the reviewer approval
                           timestamp when the legacy free-text published_at
                           isn't set on the article's data (new articles
                           no longer store that field). ISO timestamps get
                           formatted; free-text strings pass through. */
                        let displayDate = row.data?.published_at;
                        if (!displayDate && row.reviewed_at) {
                            try {
                                displayDate = format(parseISO(row.reviewed_at), "MMM yyyy");
                            } catch { /* fall through — no date */ }
                        }
                        return (
                            <ArticleRow
                                key={row.id}
                                data={{ ...row.data, published_at: displayDate }}
                                accent={accent}
                                slug={slug}
                            />
                        );
                    })}
                </div>
            )}
        </section>
    );
}

function ArticleRow({ data, accent, slug }) {
    const body = (
        <div className="flex gap-4 items-start">
            {data.cover_image ? (
                <img
                    src={data.cover_image}
                    alt=""
                    className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl object-cover border border-slate-200 shrink-0"
                />
            ) : (
                <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: accent.soft }}>
                    <Article size={26} weight="duotone" style={{ color: accent.base }} />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="font-display text-[15px] font-bold text-slate-900 leading-snug">{data.title}</div>
                {data.published_at && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        {data.published_at}
                    </div>
                )}
                <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed line-clamp-3">
                    {data.summary}
                </p>
                <div className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold group-hover:gap-1.5 transition-all" style={{ color: accent.base }}>
                    Read article <ArrowRight size={12} weight="bold" />
                </div>
            </div>
        </div>
    );

    // iter100.44 — Prefer the internal dedicated URL when we have a slug
    // (indexed by Google, shareable, tracks conversion). Fallback: external
    // link if the doctor added one. Last-resort: static card.
    if (slug && data.slug) {
        return (
            <Link
                to={`/dr/${slug}/article/${data.slug}`}
                className="block rounded-xl border border-slate-200 p-3 hover:border-slate-300 hover:bg-slate-50 transition-all group"
                data-testid="landing-article-row"
            >
                {body}
            </Link>
        );
    }
    if (data.url) {
        return (
            <a
                href={data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-xl border border-slate-200 p-3 hover:border-slate-300 hover:bg-slate-50 transition-all group"
                data-testid="landing-article-row"
            >
                {body}
            </a>
        );
    }
    return (
        <div className="rounded-xl border border-slate-200 p-3" data-testid="landing-article-row">
            {body}
        </div>
    );
}

/* =========================================================================
   CASE STUDIES — iter100.24 conversion-focused rewrite.
   Adds: filter chips (by concern), interactive before/after slider, data
   pills (duration · aligner count · complexity), "Book a consult like
   this" CTA per case that opens a CallbackModal with pre-filled context,
   and a "Patient consent given" trust badge.
   ========================================================================= */
const CONCERN_META = {
    crowding:  { label: "Crowding" },
    spacing:   { label: "Spacing / Gaps" },
    overbite:  { label: "Overbite" },
    underbite: { label: "Underbite" },
    complex:   { label: "Complex" },
    other:     { label: "Other" },
};
const COMPLEXITY_META = {
    mild:     { label: "Mild",     color: "hsl(142 60% 40%)" },
    moderate: { label: "Moderate", color: "hsl(38 92% 45%)"  },
    complex:  { label: "Complex",  color: "hsl(0 75% 50%)"   },
};

function CaseStudiesSection({ items, accent, doc, slug }) {
    const [filter, setFilter] = useState("all");

    /* Concern chips are only rendered for concerns actually present in the
       doctor's case list — no point offering "Underbite" when they have
       zero underbite cases. */
    const availableConcerns = useMemo(() => {
        const set = new Set();
        items.forEach((row) => {
            const c = row.data?.concern_type;
            if (c && CONCERN_META[c]) set.add(c);
        });
        return Array.from(set);
    }, [items]);

    const filtered = filter === "all"
        ? items
        : items.filter((row) => (row.data?.concern_type || "other") === filter);

    return (
        <section id="case_studies" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7" data-testid="landing-case-studies-card">
            <div className="flex items-center gap-2 mb-4">
                <SectionIcon><Sparkle size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                <h2 className="font-display text-lg font-bold text-slate-900">Case Studies</h2>
                {items.length > 0 && (
                    <span className="ml-1 text-[11px] text-slate-500 font-semibold">· {items.length}</span>
                )}
            </div>

            {/* Filter chips — only appear when the doctor has 2+ concerns
                to filter across (otherwise the chip row is pointless). */}
            {availableConcerns.length >= 2 && (
                <div className="flex items-center gap-1.5 flex-wrap mb-5" data-testid="landing-case-filters">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 mr-1">
                        <Funnel size={11} weight="duotone" /> Filter:
                    </span>
                    <FilterChip
                        active={filter === "all"}
                        onClick={() => setFilter("all")}
                        accent={accent}
                        testId="landing-case-filter-all"
                    >
                        All ({items.length})
                    </FilterChip>
                    {availableConcerns.map((c) => {
                        const count = items.filter((row) => (row.data?.concern_type || "other") === c).length;
                        return (
                            <FilterChip
                                key={c}
                                active={filter === c}
                                onClick={() => setFilter(c)}
                                accent={accent}
                                testId={`landing-case-filter-${c}`}
                            >
                                {CONCERN_META[c].label} ({count})
                            </FilterChip>
                        );
                    })}
                </div>
            )}

            {items.length === 0 ? (
                <EmptyState
                    icon={<Sparkle size={22} weight="duotone" style={{ color: accent.base }} />}
                    text={`${shortDoctorName(doc?.name) || "This provider"} hasn\u2019t shared any patient transformations yet.`}
                    accent={accent}
                />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={<Funnel size={22} weight="duotone" style={{ color: accent.base }} />}
                    text="No cases match this filter. Try 'All' or a different concern."
                    accent={accent}
                />
            ) : (
                <div className="space-y-6">
                    {filtered.map((row) => (
                        <CaseStudyRow
                            key={row.id}
                            data={row.data}
                            accent={accent}
                            doctorName={doc?.name}
                            slug={slug}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

function FilterChip({ active, onClick, accent, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="h-7 px-3 rounded-full text-[11px] font-semibold border transition-all"
            style={active
                ? { backgroundColor: accent.base, borderColor: accent.base, color: "white" }
                : { backgroundColor: "white", borderColor: "hsl(215 20% 90%)", color: "hsl(215 20% 40%)" }
            }
            data-testid={testId}
        >
            {children}
        </button>
    );
}

function CaseStudyRow({ data, accent, doctorName, slug }) {
    // iter100.44 — Individual case study URL, opened when clicking the
    // title or the "Read full case" link. Missing slug → static text.
    // iter100.46 (post-review) — The "doctorName" prop is unused inside
    // the row itself but retained for API stability with the parent
    // section which passes it for future in-row personalisation.
    // iter100.49 — Whole card is now clickable (parity with ArticleRow).
    // The before/after slider gets `stopPropagation` on click so dragging
    // the divider doesn't inadvertently navigate away.
    void doctorName;
    const caseUrl = slug && data.slug ? `/dr/${slug}/case/${data.slug}` : null;
    const body = (
        <>
            {/* Interactive before/after slider. Falls back to a labeled
                single-image cell if only one side is uploaded. */}
            {(data.before_image || data.after_image) && (
                <div
                    className="relative"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                >
                    {data.before_image && data.after_image ? (
                        <BeforeAfterSlider before={data.before_image} after={data.after_image} />
                    ) : (
                        <div className="relative aspect-[4/3] bg-slate-100">
                            <img
                                src={data.before_image || data.after_image}
                                alt={data.before_image ? "Before" : "After"}
                                className="h-full w-full object-cover"
                            />
                            <div
                                className="absolute top-3 left-3 h-6 px-2 rounded-md inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider"
                                style={{ backgroundColor: data.before_image ? "rgba(15,23,42,0.75)" : accent.base, color: "white" }}
                            >
                                {data.before_image ? "Before" : "After"}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div
                            className={`font-display text-[16px] font-bold text-slate-900 leading-tight ${caseUrl ? "group-hover:underline decoration-2 underline-offset-2" : ""}`}
                            style={caseUrl ? { textDecorationColor: accent.base } : undefined}
                            data-testid="landing-case-title-link"
                        >
                            {data.title}
                        </div>
                        <div className="text-[12px] text-slate-600 mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap">
                            {data.treatment && (
                                <span className="inline-flex items-center gap-1">
                                    <Stethoscope size={12} weight="duotone" style={{ color: accent.base }} />
                                    {data.treatment}
                                </span>
                            )}
                        </div>
                    </div>
                    {/* iter100.24 — Patient-consent trust badge. Only shown
                        when the doctor explicitly ticked the consent box in
                        the editor; helps address the natural viewer
                        question of "how'd they get permission to show this?". */}
                    {data.patient_consent_given !== false && (
                        <span
                            className="shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-bold uppercase tracking-wider border"
                            style={{ backgroundColor: "hsl(142 65% 96%)", borderColor: "hsl(142 65% 85%)", color: "hsl(142 60% 30%)" }}
                            title="Patient provided written consent to publish"
                            data-testid="landing-case-consent-badge"
                        >
                            <ShieldCheck size={10} weight="fill" /> Patient consent
                        </span>
                    )}
                </div>

                {/* Data pills row */}
                {(data.duration || data.aligner_count || data.complexity || data.concern_type) && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {data.concern_type && CONCERN_META[data.concern_type] && (
                            <DataPill
                                icon={<Sparkle size={11} weight="fill" />}
                                label={CONCERN_META[data.concern_type].label}
                                bg={accent.soft}
                                border={accent.ring}
                                color={accent.text}
                            />
                        )}
                        {data.duration && (
                            <DataPill
                                icon={<Clock size={11} weight="fill" />}
                                label={data.duration}
                            />
                        )}
                        {data.aligner_count > 0 && (
                            <DataPill
                                icon={<Ruler size={11} weight="fill" />}
                                label={`${data.aligner_count} aligners`}
                            />
                        )}
                        {data.complexity && COMPLEXITY_META[data.complexity] && (
                            <DataPill
                                icon={<CheckCircle size={11} weight="fill" />}
                                label={`${COMPLEXITY_META[data.complexity].label} complexity`}
                                color={COMPLEXITY_META[data.complexity].color}
                            />
                        )}
                    </div>
                )}

                <p className="text-[13px] text-slate-700 mt-3 leading-relaxed whitespace-pre-wrap line-clamp-6">{data.description}</p>
                {caseUrl && (
                    <span
                        className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-semibold group-hover:gap-1.5 transition-all"
                        style={{ color: accent.base }}
                        data-testid="landing-case-read-full"
                    >
                        Read full case study <ArrowRight size={11} weight="bold" />
                    </span>
                )}
                {/* iter100.46 (post-review) — Removed the per-row "Book a
                    consult like this" CTA. Each case card was carrying
                    both a "Read full case study →" link AND a full-width
                    "Similar to your case? · [Book a consult like this]"
                    button, which visually stacked into a duplicate CTA
                    strip on every case. The main page-level Book CTA
                    (top nav + sticky sidebar on the dedicated case
                    page) already covers the intent; per-card noise is
                    gone. */}
            </div>
        </>
    );

    if (caseUrl) {
        return (
            <Link
                to={caseUrl}
                className="block rounded-xl border border-slate-200 overflow-hidden hover:border-slate-300 hover:shadow-sm transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ "--tw-ring-color": accent.base }}
                aria-label={`Read case study: ${data.title}`}
                data-testid="landing-case-study-row"
            >
                {body}
            </Link>
        );
    }
    return (
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="landing-case-study-row">
            {body}
        </div>
    );
}

function DataPill({ icon, label, bg = "hsl(215 20% 96%)", border = "hsl(215 20% 90%)", color = "hsl(215 20% 30%)" }) {
    return (
        <span
            className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-semibold border"
            style={{ backgroundColor: bg, borderColor: border, color }}
        >
            {icon}{label}
        </span>
    );
}

/* =========================================================================
   TESTIMONIALS — patient quotes. Uses a quote-styled card grid.
   ========================================================================= */
function TestimonialsSection({ items, accent, doctorName }) {
    /* iter100.31 — Public "Reviews" tab. Doctors no longer author
       testimonials (major DCI/ASCI/DPDP compliance issue). Phase 2 ships
       automated verified reviews sourced from real Invisique bookings.

       iter100.43 — Until Phase 2 ships, we DO surface any legacy /
       admin-seeded testimonials so the section isn't visually empty and
       the doctor can see how the reviews UX will render once verification
       goes live. A small header note tags them as pre-verification. */
    const rows = (items || []).filter((it) => (it?.data?.quote || "").trim().length > 0);

    if (rows.length === 0) {
        // No content at all — show the "coming soon" placeholder.
        return (
            <section id="testimonials" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7" data-testid="landing-testimonials-card">
                <div className="flex items-center gap-2 mb-4">
                    <SectionIcon><ChatCircleText size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                    <h2 className="font-display text-lg font-bold text-slate-900">Verified Patient Reviews</h2>
                </div>
                <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center" data-testid="reviews-coming-soon">
                    <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: accent.soft }}>
                        <ChatCircleText size={22} weight="duotone" style={{ color: accent.base }} />
                    </div>
                    <div className="font-display text-base font-bold text-slate-900">Verified reviews coming soon</div>
                    <p className="text-[13px] text-slate-600 mt-1.5 max-w-md mx-auto leading-relaxed">
                        We ask every Invisique™ patient to rate their visit 3&ndash;5 days after their appointment.
                        Verified reviews from {doctorName || "this provider"}&apos;s patients will appear here automatically &mdash; no fake or self-written testimonials.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md border" style={{ backgroundColor: "white", borderColor: accent.ring, color: accent.text }}>
                        <ShieldCheck size={11} weight="fill" />
                        Only Invisique-verified reviews shown
                    </div>
                </div>
            </section>
        );
    }

    // Render seeded / legacy testimonials with a preview banner
    const avgRating = (() => {
        const rated = rows.filter((r) => typeof r?.data?.rating === "number" && r.data.rating > 0);
        if (rated.length === 0) return null;
        return (rated.reduce((s, r) => s + r.data.rating, 0) / rated.length).toFixed(1);
    })();

    return (
        <section id="testimonials" className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7" data-testid="landing-testimonials-card">
            <div className="flex items-center gap-2 mb-1">
                <SectionIcon><ChatCircleText size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                <h2 className="font-display text-lg font-bold text-slate-900">
                    Patient reviews <span className="text-slate-400 font-medium">· {rows.length}</span>
                </h2>
                {avgRating && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[12px] font-bold text-amber-700">
                        <Star size={12} weight="fill" className="text-amber-500" /> {avgRating}
                    </span>
                )}
            </div>
            <p className="text-[11.5px] text-muted-foreground mb-4">
                {/* iter100.172 — Positive subheading framing per user
                    request. Was: "Patients treated by {name} — soon to be
                    auto-collected via SMS…" (mixed a factual bit with a
                    coming-soon disclaimer). "Why Patients Choose {name}"
                    reads as a confidence hook and matches how top health
                    portals frame the reviews block. */}
                Why Patients Choose {doctorName || "this provider"}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {rows.map((row) => (
                    <TestimonialRow key={row.id} data={row.data || {}} accent={accent} />
                ))}
            </div>
        </section>
    );
}

function TestimonialRow({ data, accent }) {
    return (
        <div className="rounded-xl border border-slate-200 p-4 relative" data-testid="landing-testimonial-row">
            <Quotes size={18} weight="fill" className="absolute top-3 right-3 opacity-15" style={{ color: accent.base }} />
            {typeof data.rating === "number" && data.rating > 0 && (
                <div className="flex items-center gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                            key={n}
                            size={13}
                            weight={n <= data.rating ? "fill" : "regular"}
                            style={{ color: n <= data.rating ? "hsl(38 92% 50%)" : "hsl(215 20% 80%)" }}
                        />
                    ))}
                </div>
            )}
            <p className="text-[13px] text-slate-700 leading-relaxed italic">&ldquo;{data.quote}&rdquo;</p>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                <div>
                    <div className="text-[12px] font-semibold text-slate-800">
                        {data.is_anonymous ? initials(data.patient_display_name) : data.patient_display_name}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                        {data.treatment && <span>{data.treatment}</span>}
                        {data.treatment_date && (
                            <>
                                {data.treatment && <span>·</span>}
                                <span>{data.treatment_date}</span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function initials(name) {
    if (!name) return "Anonymous";
    // "Priya Sharma" → "Priya S." — safer PII footprint for anon quotes.
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

/* iter100.63 — Facility-key → patient-facing one-line description map.
   Used by the Clinic tab's rich facility tiles. Falls back to the chip
   label if a key is missing (shouldn't happen — full taxonomy covered). */
const FACILITY_DESCRIPTIONS = {
    kids_friendly:         "Kid-friendly waiting area with toys — great for family visits.",
    same_day:              "Same-day consult slots for urgent smile queries.",
    late_evening_hours:    "Consult after 7 PM — convenient for working professionals.",
    wheelchair_accessible: "Step-free entry and accessible washroom on-site.",
    female_practitioner:   "Female clinician available at this practice.",
    emergency_care:        "Same-day emergency care for aligner mishaps and pain.",
    home_visits:           "Home visits for scans and impressions in select cases.",
    virtual_consult:       "Video consultations — get a plan without leaving home.",
    parking_available:     "On-site parking so you never hunt for a spot.",
    washroom_available:    "Clean washroom facilities on the clinic floor.",
    refreshment_station:   "Complimentary tea, coffee and water while you wait.",
    free_wifi:             "Free high-speed Wi-Fi in the waiting area.",
    emi_available:         "Split treatment cost into 0% interest EMIs.",
    lgbtq_friendly:        "Welcoming, inclusive care regardless of gender identity.",
    weekend_hours:         "Open on weekends — no need to skip work for a visit.",
    three_d_scanner:       "In-clinic 3D iTero scanner — precise digital impressions.",
    multi_clinic:          "Multiple clinic locations for your convenience.",
    additional_languages:  "Consult in your preferred language — multilingual practice.",
};

/* =========================================================================
   CLINIC TAB — iter100.64 simplified.
   Streamlined to three sections: Photos (top), First-visit walkthrough,
   and richly-described In-Clinic Facility tiles. Consultation fee,
   opening hours, address & contact CTAs live in the right sidebar
   (BookingSidebar) — no need to duplicate them here.
   ========================================================================= */
function ClinicGalleryCard({ doc, clinic, accent }) {
    const [lightbox, setLightbox] = useState(null);
    const photos = doc.clinic_photos || [];
    const clinicName = clinic?.clinic_name || "This clinic";
    const chips = doc.chips || [];
    const facilities = chips.filter(
        (c) => (c.tier || 2) === 2 && c.source !== "auto" && c.source !== "admin",
    );

    return (
        <section
            id="clinic"
            className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 space-y-8"
            data-testid="landing-clinic-tab"
        >
            {/* Section title */}
            <header className="flex items-center gap-2">
                <SectionIcon><Buildings size={16} weight="duotone" style={{ color: accent.base }} /></SectionIcon>
                <h2 className="font-display text-lg font-bold text-slate-900">Inside the clinic</h2>
            </header>

            {/* Clinic photo grid — top of the tab */}
            <div data-testid="clinic-photo-section">
                {photos.length === 0 ? (
                    <EmptyState
                        icon={<Buildings size={22} weight="duotone" style={{ color: accent.base }} />}
                        text={`${clinicName} hasn't uploaded clinic photos yet.`}
                        accent={accent}
                    />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="landing-gallery-card">
                        {photos.map((src, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setLightbox(src)}
                                className="aspect-square overflow-hidden rounded-xl bg-slate-100 group border border-slate-200"
                            >
                                <img
                                    src={src}
                                    alt={`${clinicName} ${i + 1}`}
                                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* First-visit walkthrough — reduces booking anxiety */}
            <div data-testid="clinic-first-visit-walkthrough">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                    What to expect on your first visit
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <FirstVisitStep
                        step={1}
                        icon={<ChatCircleText size={18} weight="duotone" style={{ color: accent.base }} />}
                        title="Consultation"
                        body="Sit-down with the doctor to discuss your smile goals, medical history, and photos."
                        accent={accent}
                    />
                    <FirstVisitStep
                        step={2}
                        icon={<ScanSmiley size={18} weight="duotone" style={{ color: accent.base }} />}
                        title="3D Digital Scan"
                        body="Painless in-clinic scan — no messy impressions. Takes ~10 minutes."
                        accent={accent}
                    />
                    <FirstVisitStep
                        step={3}
                        icon={<ClipboardText size={18} weight="duotone" style={{ color: accent.base }} />}
                        title="Treatment Plan"
                        body="Preview your smile transformation and receive a personalised aligner plan & quote."
                        accent={accent}
                    />
                </div>
            </div>

            {/* In-Clinic Facilities — rich tiles with descriptions */}
            {facilities.length > 0 && (
                <div data-testid="clinic-facility-tiles">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
                        In-Clinic Facilities
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {facilities.map((f) => (
                            <FacilityTile key={f.key} chip={f} accent={accent} />
                        ))}
                    </div>
                </div>
            )}

            {lightbox && (
                <div
                    className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
                    onClick={() => setLightbox(null)}
                    role="dialog"
                >
                    <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] rounded-2xl" />
                </div>
            )}
        </section>
    );
}

function FirstVisitStep({ step, icon, title, body, accent }) {
    return (
        <div
            className="rounded-xl border border-slate-200 p-4 relative overflow-hidden"
            data-testid={`clinic-first-visit-step-${step}`}
        >
            <div
                className="absolute top-2 right-3 text-[36px] font-black leading-none opacity-[0.06] select-none"
                aria-hidden
                style={{ color: accent.text }}
            >
                {step}
            </div>
            <div className="relative flex items-start gap-3">
                <div
                    className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: accent.soft }}
                >
                    {icon}
                </div>
                <div className="min-w-0">
                    <div className="text-[13px] font-bold text-slate-900">{title}</div>
                    <div className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">{body}</div>
                </div>
            </div>
        </div>
    );
}

function FacilityTile({ chip, accent }) {
    const description = FACILITY_DESCRIPTIONS[chip.key] || chip.label;
    return (
        <div
            className="rounded-xl border border-slate-200 p-4 flex items-start gap-3 hover:border-slate-300 transition-colors"
            data-testid={`clinic-facility-tile-${chip.key}`}
        >
            <div
                className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: "hsl(150 55% 96%)", color: "hsl(150 60% 28%)" }}
            >
                <PublicChipIcon name={chip.icon} size={18} />
            </div>
            <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-slate-900">{chip.label}</div>
                <div className="mt-0.5 text-[12px] text-slate-600 leading-relaxed">{description}</div>
            </div>
        </div>
    );
}

function formatSlotTime(t) {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const suffix = h < 12 ? "AM" : "PM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

/* iter100.64 — Title-case helper for free-text user input that patients
   see (e.g. languages list "english, hindi" → "English, Hindi"). Only
   uppercases the first character of each word; safe on ALL-CAPS input
   (leaves "URDU" → "Urdu"). Empty / non-string returns "" so it can be
   composed inside .map(_titleCase). */
function _titleCase(s) {
    if (typeof s !== "string" || !s) return "";
    return s
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

/* iter100.64 — Business-hours utilities for the sidebar's live "Open now"
   indicator. Schema (from `doctor.business_hours`):
     { mon: {open:"09:00", close:"18:00", closed:false}, ..., sun: {...} } */
const _DOW_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const _DOW_SHORT = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

function _nextOpenDay(hours, todayKey) {
    const idx = _DOW_ORDER.indexOf(todayKey);
    for (let step = 1; step <= 7; step++) {
        const next = _DOW_ORDER[(idx + step) % 7];
        const cfg = hours[next];
        // iter100.65 — Require both open + close (partial rows are
        // treated as unconfigured; see computeOpenStatus).
        if (cfg && !cfg.closed && cfg.open && cfg.close) return next;
    }
    return null;
}

function computeOpenStatus(hours) {
    /* Returns { open, primary, detail }.
       open === null → hours not populated, caller should fall back.
       open true/false → live status; `primary` is short one-liner,
       `detail` is a secondary line (e.g. tomorrow's opening time).

       iter100.65 — Treats partially-populated rows (missing `open` or
       `close`) as NOT configured for that specific day rather than
       silently falling through as "closed". Prevents the microsite
       from showing "Closed today" on days the doctor thinks they've
       set. See BusinessHoursEditor for the write-side normalization. */
    if (!hours || Object.keys(hours).length === 0) return { open: null, primary: "", detail: "" };
    const now = new Date();
    const dow = _DOW_ORDER[(now.getDay() + 6) % 7]; // JS Sunday=0; shift so Mon=0
    const cfg = hours[dow];
    const nowHM = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const rowValid = (r) => r && (r.closed || (r.open && r.close));
    if (!rowValid(cfg)) {
        const nextKey = _nextOpenDay(hours, dow);
        const nextCfg = nextKey ? hours[nextKey] : null;
        return {
            open: false,
            primary: "By appointment today",
            detail: nextCfg ? `Next scheduled: ${_DOW_SHORT[nextKey]} at ${formatSlotTime(nextCfg.open)}` : "Contact clinic for slot",
        };
    }
    if (cfg.closed) {
        const nextKey = _nextOpenDay(hours, dow);
        const nextCfg = nextKey ? hours[nextKey] : null;
        return {
            open: false,
            primary: "Closed today",
            detail: nextCfg ? `Opens ${_DOW_SHORT[nextKey]} at ${formatSlotTime(nextCfg.open)}` : "Reopens soon",
        };
    }
    if (nowHM >= cfg.open && nowHM < cfg.close) {
        return { open: true, primary: "Open now", detail: `Closes at ${formatSlotTime(cfg.close)}` };
    }
    if (nowHM < cfg.open) {
        return { open: false, primary: "Opens later", detail: `Opens today at ${formatSlotTime(cfg.open)}` };
    }
    const nextKey = _nextOpenDay(hours, dow);
    const nextCfg = nextKey ? hours[nextKey] : null;
    return {
        open: false,
        primary: "Closed for today",
        detail: nextCfg ? `Opens ${_DOW_SHORT[nextKey]} at ${formatSlotTime(nextCfg.open)}` : "Reopens soon",
    };
}

/* =========================================================================
   CALLBACK MODAL — the "Request a Callback" quick lead-capture. Two-step
   form: (1) name + phone + notes + consent → creates the attributed lead,
   (2) confirmation screen with "we'll call within 30 min".
   Reuses POST /api/counsellor-callback (now attribution-aware).
   ========================================================================= */
export function CallbackModal({ doc, slug, accent, onClose, defaultNotes = "" }) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [notes, setNotes] = useState(defaultNotes);
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const firstFieldRef = useRef(null);

    useEffect(() => {
        firstFieldRef.current?.focus();
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    const cleanPhone = phone.replace(/[^\d]/g, "");
    const canSubmit = name.trim().length >= 2 && cleanPhone.length >= 10 && consent && !submitting;

    const submit = async () => {
        if (!canSubmit) {
            if (name.trim().length < 2) return toast.error("Please enter your full name.");
            if (cleanPhone.length < 10) return toast.error("Enter a valid 10-digit mobile number.");
            if (!consent) return toast.error("Please agree to the contact consent to continue.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/counsellor-callback", {
                name: name.trim(),
                phone: cleanPhone,
                notes: notes.trim(),
                consent_given: true,
                attributed_doctor_id: doc.doctor_id,
            });
            api.post("/public/doctor-page/event", { kind: "lead", slug }).catch(() => {});
            setDone(true);
        } catch {
            toast.error("Couldn't submit — please try again in a moment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-6"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
            data-testid="landing-callback-modal"
        >
            <div className="bg-white w-full sm:max-w-md max-h-[92vh] sm:max-h-[86vh] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-2 fade-in duration-300">
                {!done ? (
                    <>
                        {/* Header — never scrolls */}
                        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent.base }}>Request a callback</div>
                                <div className="font-display text-lg font-bold text-slate-900 leading-tight mt-0.5">
                                    We&apos;ll call you back
                                </div>
                                <div className="text-[13px] text-slate-600 mt-1 leading-snug">
                                    A counsellor from <span className="font-semibold text-slate-800">{doc.name}</span>&apos;s clinic will call within 30 minutes.
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center shrink-0"
                                aria-label="Close"
                                data-testid="landing-callback-close"
                            >
                                <X size={16} weight="bold" />
                            </button>
                        </div>

                        {/* Fields — flex-1 scrolls when needed */}
                        <div className="p-5 space-y-4 flex-1 min-h-0 overflow-y-auto">
                            <div>
                                <Label htmlFor="cb-name" className="text-[12px] font-semibold text-slate-700">Your name *</Label>
                                <Input
                                    id="cb-name"
                                    ref={firstFieldRef}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Priya Sharma"
                                    className="mt-1"
                                    data-testid="landing-callback-name"
                                />
                            </div>
                            <div>
                                <Label htmlFor="cb-phone" className="text-[12px] font-semibold text-slate-700">Phone *</Label>
                                <Input
                                    id="cb-phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="10-digit mobile number"
                                    inputMode="tel"
                                    className="mt-1"
                                    data-testid="landing-callback-phone"
                                />
                                <div className="text-[11px] text-slate-500 mt-1">
                                    No spam · One call, that&apos;s it.
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="cb-notes" className="text-[12px] font-semibold text-slate-700">Anything you&apos;d like to share? <span className="text-slate-400 font-normal">(optional)</span></Label>
                                <Textarea
                                    id="cb-notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Your concerns, questions, or preferred time to talk…"
                                    rows={3}
                                    className="mt-1 resize-none"
                                    data-testid="landing-callback-notes"
                                />
                            </div>
                            <label className="flex items-start gap-2 cursor-pointer group" data-testid="landing-callback-consent-row">
                                <input
                                    type="checkbox"
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-slate-300 shrink-0"
                                    style={{ accentColor: accent.base }}
                                    data-testid="landing-callback-consent"
                                />
                                <span className="text-[12px] text-slate-600 leading-snug select-none">
                                    I agree to be contacted by Invisique™ via call, SMS, or WhatsApp about my smile consultation. I understand I can opt out anytime.
                                </span>
                            </label>
                        </div>

                        {/* Footer — never scrolls */}
                        <div className="p-5 border-t border-slate-100 bg-slate-50 shrink-0">
                            <Button
                                className="w-full h-11 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                                disabled={!canSubmit}
                                onClick={submit}
                                style={canSubmit ? { backgroundColor: accent.base, color: "white" } : undefined}
                                data-testid="landing-callback-submit"
                            >
                                {submitting ? "Sending…" : (
                                    <>
                                        <PhoneCall size={14} weight="fill" className="mr-1" /> Request the callback
                                    </>
                                )}
                            </Button>
                            <div className="mt-2.5 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
                                <ShieldCheck size={12} weight="duotone" style={{ color: accent.base }} />
                                Free · No obligation
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="p-8 text-center overflow-y-auto" data-testid="landing-callback-success">
                        <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: accent.soft }}>
                            <CheckCircle size={30} weight="fill" style={{ color: accent.base }} />
                        </div>
                        <div className="font-display text-xl font-bold text-slate-900">You&apos;re all set!</div>
                        <p className="text-[14px] text-slate-600 mt-2 leading-relaxed">
                            A counsellor from <span className="font-semibold">{doc.name}</span>&apos;s clinic will call you within 30 minutes to discuss your smile plan.
                        </p>
                        <Button
                            className="mt-6 h-11 px-6 rounded-lg font-semibold"
                            onClick={onClose}
                            style={{ backgroundColor: accent.base, color: "white" }}
                            data-testid="landing-callback-done"
                        >
                            Got it
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

/* =========================================================================
   ASSESSMENT TAB — renders the full Smile Assessment quiz inline as a tab
   (iter100.15 — was previously a modal, now consistent with Smile Preview
   which also lives inline). Uses an iframe with ?embed=1 so the site
   header/footer are hidden inside via the .embed-mode CSS class. The
   doctor attribution rides on `?doctor=` so the resulting lead lands in
   the doctor's Leads Hub with source='doctor-landing'.
   ========================================================================= */
function AssessmentTab({ doctorId, accent }) {
    return (
        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="landing-assessment-tab">
            <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center gap-3" style={{ backgroundColor: accent.soft }}>
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: accent.base, color: "white" }}>
                    <ClipboardText size={18} weight="fill" />
                </div>
                <div className="min-w-0">
                    <div className="font-display text-lg font-bold text-slate-900 leading-tight">Free Smile Assessment</div>
                    <div className="text-[13px] text-slate-600">5 quick questions · personalised treatment plan · ~2 minutes</div>
                </div>
            </div>
            {/* iter100.22 — Assessment quiz now renders INLINE as a React
                component instead of inside an <iframe>. The old iframe
                approach paid a ~500ms – 2s cost per tab click (fresh HTTP
                fetch + full React app boot inside the frame). Inline
                render is instantaneous — just a React re-mount. The
                SmileAssessment component accepts `isEmbedProp` +
                `attributedDoctorIdProp` so it applies the same compact
                styling and doctor attribution without touching the URL. */}
            <div data-testid="landing-assessment-inline">
                <SmileAssessment attributedDoctorIdProp={doctorId} isEmbedProp={true} />
            </div>
        </section>
    );
}

/* =========================================================================
   BOOKING SIDEBAR — iter100.16 compact clinic-info card.
   Removed the internal date/time picker (which duplicated the modal). The
   sidebar's job is now purely: introduce the clinic + one prominent CTA
   that opens the shared BookingFlowModal, plus Call/WhatsApp secondaries.
   ========================================================================= */
function BookingSidebar({ doc, accent, clinic, firstName, onOpenBooking }) {
    return (
        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="landing-booking-sidebar">
            {/* Clinic identity */}
            <div className="p-5">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Consult at</div>
                <div className="font-display text-base font-bold text-slate-900 leading-tight">{clinic.clinic_name || doc.name}</div>
                {(clinic.address || clinic.city) && (
                    <div className="text-[13px] text-slate-600 mt-1 leading-snug">
                        {[clinic.address, clinic.city].filter(Boolean).join(", ")}
                    </div>
                )}
                {(clinic.clinic_name || clinic.address) && (
                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([clinic.clinic_name, clinic.address, clinic.city].filter(Boolean).join(", "))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold hover:underline"
                        style={{ color: accent.base }}
                        data-testid="landing-sidebar-directions"
                    >
                        <MapPin size={12} weight="fill" /> Get directions <CaretRight size={11} />
                    </a>
                )}
                <div className="mt-3 flex items-start gap-1.5 text-[12px] text-slate-600" data-testid="landing-sidebar-hours">
                    <Clock size={13} weight="duotone" style={{ color: accent.base }} className="mt-[2px] shrink-0" />
                    {(() => {
                        const status = computeOpenStatus(doc.business_hours);
                        if (status.open === null) {
                            // Fallback when the doctor hasn't set hours yet.
                            return <span>Hours by appointment · Book to confirm</span>;
                        }
                        return (
                            <span className="leading-snug">
                                <span
                                    className={`font-semibold ${status.open ? "text-emerald-700" : "text-slate-700"}`}
                                    data-testid="landing-sidebar-hours-status"
                                >
                                    {status.primary}
                                </span>
                                <span className="text-slate-500"> · {status.detail}</span>
                            </span>
                        );
                    })()}
                </div>
            </div>

            {/* Fee + Next Available row */}
            <div className="px-5 py-4 border-y border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Consult fee</div>
                    <div className="font-display font-bold flex items-baseline gap-0.5 mt-0.5" style={{ color: accent.base }}>
                        {doc.consultation_fee > 0 ? (
                            <>
                                <CurrencyInr size={13} weight="bold" />
                                <span className="text-xl leading-none">{Number(doc.consultation_fee).toLocaleString("en-IN")}</span>
                            </>
                        ) : (
                            <span className="text-sm">FREE</span>
                        )}
                    </div>
                </div>
                <SidebarNextAvailable doctorId={doc.doctor_id} accent={accent} />
            </div>

            {/* Primary CTA + secondary quick actions */}
            <div className="p-5 space-y-3">
                <Button
                    onClick={onOpenBooking}
                    className="w-full h-11 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all"
                    style={{ backgroundColor: accent.base, color: "white" }}
                    data-testid="landing-sidebar-book-btn"
                >
                    <CalendarBlank size={14} weight="fill" className="mr-1.5" /> Book Appointment
                </Button>

                {(doc.cta_phone || doc.cta_whatsapp) && (
                    <div className="flex items-center gap-2">
                        {doc.cta_phone && (
                            <a
                                href={`tel:${doc.cta_phone}`}
                                className="flex-1 h-10 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center gap-1.5 text-[12px] font-semibold text-slate-700 transition-colors"
                                data-testid="landing-sidebar-call"
                            >
                                <Phone size={13} weight="fill" style={{ color: accent.base }} /> Call
                            </a>
                        )}
                        {doc.cta_whatsapp && (
                            <a
                                href={`https://wa.me/${doc.cta_whatsapp.replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Hi ${firstName}, I'd like to know more about Invisique™ clear aligners.`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 h-10 rounded-lg border border-emerald-200 bg-white hover:bg-emerald-50 flex items-center justify-center gap-1.5 text-[12px] font-semibold text-emerald-700 transition-colors"
                                data-testid="landing-sidebar-whatsapp"
                            >
                                <WhatsappLogo size={13} weight="fill" /> WhatsApp
                            </a>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 pt-1">
                    <ShieldCheck size={12} weight="duotone" style={{ color: accent.base }} />
                    Free to reserve · Verified via OTP
                </div>
            </div>
        </section>
    );
}

/* Compact next-available line for the sidebar (matches the header chip but
   inline-styled to fit the fee row). */
function SidebarNextAvailable({ doctorId, accent }) {
    const [next, setNext] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        api.get(`/doctors/${doctorId}/availability-summary`, { params: { days: 14 } })
            .then((r) => {
                if (cancelled) return;
                const first = Array.isArray(r.data) ? r.data.find((s) => s.has_slots) : null;
                if (first) setNext({ date: first.date, time: first.first_slot });
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [doctorId]);
    if (loading) return null;
    if (!next) {
        return <div className="text-right text-[11px] text-slate-500 max-w-[140px]">No open slots this fortnight — try WhatsApp.</div>;
    }
    const [h, m] = next.time.split(":").map(Number);
    const timeStr = `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
    return (
        <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent.base }}>Next available</div>
            <div className="text-[13px] font-semibold text-slate-800 mt-0.5">
                {format(parseISO(next.date), "EEE, d MMM")}
            </div>
            <div className="text-[11px] text-slate-600">{timeStr}</div>
        </div>
    );
}

/* =========================================================================
   BOOKING FLOW MODAL — iter100.16 the single source of truth for booking.
   Two-step flow inside one 720px (desktop) modal:

   Step 1 — Slot picker
     • Desktop: 2-column (LEFT: 14-day date list · RIGHT: time-of-day chips
       + time slot grid)
     • Mobile: single column stack (horizontal date strip on top)
     • "Next Available" quick-jump chip at top
     • "Continue to details" button (disabled until slot picked)

   Step 2 — Patient details + consent
     • "You're booking Thu 9 Jul · 9:00 AM at Bright Smiles Dental" summary
     • "← Change slot" back link
     • Name · Phone (for OTP) · Email · Notes · Consent checkbox
     • "Send OTP & Confirm" → parent handoff via onConfirm
   ========================================================================= */
function BookingFlowModal({ doc, slug, accent, clinic, onClose, onConfirm }) {
    const DAY_COUNT = 14;
    const [step, setStep] = useState(1);
    const [days] = useState(() => Array.from({ length: DAY_COUNT }, (_, i) => addDays(new Date(), i + 1)));
    const [selectedDay, setSelectedDay] = useState(days[0]);
    const [summary, setSummary] = useState(null);
    const [slots, setSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);
    const [tod, setTod] = useState("all");

    // Step 2 fields
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [notes, setNotes] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const nameFieldRef = useRef(null);

    // Body scroll lock + Esc close
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    // Availability summary once
    useEffect(() => {
        api.get(`/doctors/${doc.doctor_id}/availability-summary`, { params: { days: DAY_COUNT } })
            .then((r) => setSummary(Array.isArray(r.data) ? r.data : []))
            .catch(() => setSummary([]));
    }, [doc.doctor_id]);

    // Load slots for selected day
    const loadSlots = useCallback(async () => {
        setSlotsLoading(true);
        setSelectedTime(null);
        try {
            const dateStr = format(selectedDay, "yyyy-MM-dd");
            const { data } = await api.get(`/doctors/${doc.doctor_id}/slots?date=${dateStr}`);
            const normalized = Array.isArray(data)
                ? data
                    .map((s) => (typeof s === "string" ? s : (s?.available !== false ? s?.time : null)))
                    .filter(Boolean)
                : [];
            setSlots(normalized);
        } catch {
            setSlots([]);
        } finally {
            setSlotsLoading(false);
        }
    }, [doc.doctor_id, selectedDay]);
    useEffect(() => { loadSlots(); }, [loadSlots]);

    // Filtered slots by time-of-day
    const filteredSlots = useMemo(() => {
        if (tod === "all") return slots;
        return slots.filter((t) => {
            const h = parseInt((t || "").split(":")[0], 10);
            if (Number.isNaN(h)) return false;
            if (tod === "morning")   return h < 12;
            if (tod === "afternoon") return h >= 12 && h < 15;
            if (tod === "evening")   return h >= 15;
            return true;
        });
    }, [slots, tod]);
    const todCounts = useMemo(() => {
        const c = { morning: 0, afternoon: 0, evening: 0 };
        for (const t of slots) {
            const h = parseInt((t || "").split(":")[0], 10);
            if (Number.isNaN(h)) continue;
            if (h < 12) c.morning++;
            else if (h < 15) c.afternoon++;
            else c.evening++;
        }
        return c;
    }, [slots]);
    const nextAvailable = useMemo(() => {
        if (!summary) return null;
        const found = summary.find((s) => s.has_slots);
        if (!found) return null;
        return { date: parseISO(found.date), time: found.first_slot };
    }, [summary]);
    const summaryByDate = useMemo(() => {
        const m = {};
        (summary || []).forEach((s) => { m[s.date] = s; });
        return m;
    }, [summary]);

    // Advance to step 2
    const goToStep2 = () => {
        if (!selectedTime) return;
        setStep(2);
        setTimeout(() => nameFieldRef.current?.focus(), 60);
    };
    const backToStep1 = () => setStep(1);

    const cleanPhone = phone.replace(/[^\d]/g, "");
    const canConfirm = name.trim().length >= 2 && cleanPhone.length >= 10 && consent && !submitting;

    const confirm = () => {
        if (!canConfirm) {
            if (name.trim().length < 2) return toast.error("Please enter your full name.");
            if (cleanPhone.length < 10) return toast.error("Enter a valid 10-digit mobile number.");
            if (!consent) return toast.error("Please agree to be contacted to continue.");
            return;
        }
        setSubmitting(true);
        onConfirm({
            date: selectedDay,
            time: selectedTime,
            name: name.trim(),
            phone: cleanPhone,
            email: email.trim(),
            notes: notes.trim(),
            consent_given: true,
        });
    };

    const firstName = (() => {
        const parts = (doc.name || "").split(/\s+/).filter(Boolean);
        return (parts[0]?.replace(/\.$/, "").toLowerCase() === "dr" ? parts[1] : parts[0]) || "the doctor";
    })();

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/70 backdrop-blur-sm p-0 sm:p-6"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
            data-testid="landing-booking-modal"
        >
            <div className="bg-white w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[86vh] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-6 sm:slide-in-from-bottom-2 fade-in duration-300">
                {/* HEADER — clinic + step indicator */}
                <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between gap-3 shrink-0" style={{ backgroundColor: accent.soft }}>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent.text }}>
                            Book with {doc.name}
                        </div>
                        <div className="font-display text-base sm:text-lg font-bold text-slate-900 leading-tight mt-0.5 truncate">
                            {step === 1 ? "Pick a date & time" : `Confirm your appointment`}
                        </div>
                        {clinic.clinic_name && (
                            <div className="text-[12px] text-slate-600 mt-0.5 truncate">
                                {clinic.clinic_name}{clinic.city && ` · ${clinic.city}`}
                            </div>
                        )}
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                        <StepBadge n={1} active={step === 1} done={step > 1} accent={accent} />
                        <div className="h-px w-8" style={{ backgroundColor: step > 1 ? accent.base : "hsl(215 20% 85%)" }} />
                        <StepBadge n={2} active={step === 2} accent={accent} />
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg hover:bg-white/60 flex items-center justify-center shrink-0"
                        aria-label="Close"
                        data-testid="landing-booking-close"
                    >
                        <X size={16} weight="bold" />
                    </button>
                </div>

                {/* BODY */}
                {step === 1 ? (
                    <div className="flex-1 min-h-0 flex flex-col sm:flex-row overflow-hidden" data-testid="landing-booking-step1">
                        {/* LEFT: Date list */}
                        <div className="sm:w-56 shrink-0 sm:border-r border-slate-100 p-4 sm:overflow-y-auto">
                            {nextAvailable && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const target = days.find((d) => isSameDay(d, nextAvailable.date));
                                        if (target) {
                                            setSelectedDay(target);
                                            setSelectedTime(nextAvailable.time);
                                            setTod("all");
                                        }
                                    }}
                                    className="w-full flex items-center gap-2 p-2.5 mb-3 rounded-lg border hover:shadow-sm transition-all text-left"
                                    style={{ backgroundColor: accent.soft, borderColor: accent.ring }}
                                    data-testid="landing-booking-next-available"
                                >
                                    <Sparkle size={14} weight="fill" style={{ color: accent.base }} className="shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent.base }}>Fastest</div>
                                        <div className="text-[12px] font-semibold text-slate-800 leading-tight truncate">
                                            {format(nextAvailable.date, "EEE, d MMM")}
                                        </div>
                                    </div>
                                </button>
                            )}
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Choose a date</div>
                            {/* Mobile: horizontal strip · Desktop: vertical list */}
                            <div className="flex sm:flex-col gap-2 sm:gap-1.5 overflow-x-auto sm:overflow-x-visible no-scrollbar">
                                {days.map((d) => {
                                    const active = isSameDay(d, selectedDay);
                                    const dateStr = format(d, "yyyy-MM-dd");
                                    const sum = summaryByDate[dateStr];
                                    const isDim = sum && !sum.has_slots;
                                    return (
                                        <button
                                            key={d.toISOString()}
                                            type="button"
                                            onClick={() => setSelectedDay(d)}
                                            className="shrink-0 sm:shrink text-left rounded-lg py-2 px-2.5 transition-all border flex sm:flex-row flex-col items-start sm:items-center gap-1 sm:gap-2 min-w-[56px]"
                                            style={active
                                                ? { backgroundColor: accent.base, borderColor: accent.base, color: "white" }
                                                : isDim
                                                    ? { backgroundColor: "hsl(215 15% 97%)", borderColor: "hsl(215 15% 92%)", color: "hsl(215 15% 55%)" }
                                                    : { backgroundColor: "white", borderColor: "hsl(215 20% 88%)", color: "hsl(215 20% 30%)" }}
                                            data-testid={`landing-booking-day-${dateStr}`}
                                        >
                                            <div className="text-[10px] uppercase font-bold opacity-80 sm:hidden">{format(d, "EEE")}</div>
                                            <div className="hidden sm:block text-[11px] font-bold uppercase tracking-wider opacity-80 w-9">{format(d, "EEE")}</div>
                                            <div className="text-base font-bold sm:text-sm sm:font-bold flex-1">{format(d, "d")} <span className="hidden sm:inline text-[11px] font-normal opacity-70">{format(d, "MMM")}</span></div>
                                            <div className="text-[9px] opacity-80 sm:hidden">{format(d, "MMM")}</div>
                                            {sum && sum.has_slots && (
                                                <div className={`text-[10px] font-semibold ${active ? "opacity-80" : ""}`} style={{ color: active ? "white" : accent.base }}>
                                                    {sum.slot_count}
                                                </div>
                                            )}
                                            {isDim && <div className="text-[9px] font-medium">Full</div>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* RIGHT: Time-of-day chips + Time slot grid */}
                        <div className="flex-1 min-h-0 p-4 sm:p-5 overflow-y-auto">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Choose a time · {format(selectedDay, "EEEE, d MMMM")}</div>
                            {slots.length > 0 && (
                                <div className="flex items-center gap-1.5 flex-wrap mb-4">
                                    {[
                                        { k: "all",       label: "All",       icon: null,                                    count: slots.length },
                                        { k: "morning",   label: "Morning",   icon: <Coffee size={11} weight="fill" />,      count: todCounts.morning },
                                        { k: "afternoon", label: "Afternoon", icon: <Sun    size={11} weight="fill" />,      count: todCounts.afternoon },
                                        { k: "evening",   label: "Evening",   icon: <Moon   size={11} weight="fill" />,      count: todCounts.evening },
                                    ].map((chip) => {
                                        const active = tod === chip.k;
                                        const disabled = chip.count === 0 && chip.k !== "all";
                                        return (
                                            <button
                                                key={chip.k}
                                                type="button"
                                                onClick={() => !disabled && setTod(chip.k)}
                                                disabled={disabled}
                                                className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-semibold border transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                                style={active
                                                    ? { backgroundColor: accent.base, borderColor: accent.base, color: "white" }
                                                    : { backgroundColor: "white", borderColor: "hsl(215 20% 88%)", color: "hsl(215 20% 30%)" }}
                                                data-testid={`landing-booking-tod-${chip.k}`}
                                            >
                                                {chip.icon}{chip.label}
                                                <span className={active ? "opacity-80" : "text-slate-400"}>· {chip.count}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="min-h-[8rem]">
                                {slotsLoading && <div className="text-sm text-slate-500 text-center py-6">Loading slots…</div>}
                                {!slotsLoading && filteredSlots.length === 0 && (
                                    <div className="text-sm text-slate-500 text-center py-6">
                                        {slots.length === 0 ? "No slots on this day. Try another." : "No slots in this time-of-day. Pick another."}
                                    </div>
                                )}
                                {!slotsLoading && filteredSlots.length > 0 && (
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                                        {filteredSlots.map((t) => {
                                            const active = t === selectedTime;
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => setSelectedTime(t)}
                                                    className="px-2 h-10 rounded-lg text-[13px] font-semibold transition-all border"
                                                    style={active
                                                        ? { backgroundColor: accent.base, borderColor: accent.base, color: "white" }
                                                        : { backgroundColor: "white", borderColor: "hsl(215 20% 88%)", color: "hsl(215 20% 30%)" }}
                                                    data-testid={`landing-booking-slot-${t}`}
                                                >
                                                    {formatSlotTime(t)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Step 2 — Patient details */
                    <div className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6" data-testid="landing-booking-step2">
                        {/* Slot summary badge */}
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: accent.base, color: "white" }}>
                                <CalendarBlank size={18} weight="fill" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Your slot</div>
                                <div className="text-sm font-bold text-slate-900 leading-tight">
                                    {format(selectedDay, "EEE, d MMM")} · {formatSlotTime(selectedTime)}
                                </div>
                                {clinic.clinic_name && (
                                    <div className="text-[11px] text-slate-600 mt-0.5">at {clinic.clinic_name}</div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={backToStep1}
                                className="text-[12px] font-semibold hover:underline shrink-0"
                                style={{ color: accent.base }}
                                data-testid="landing-booking-change-slot"
                            >
                                Change
                            </button>
                        </div>

                        {/* Patient info form */}
                        <div className="mt-5 space-y-4 max-w-md mx-auto">
                            <div>
                                <Label htmlFor="bfm-name" className="text-[12px] font-semibold text-slate-700">Full name *</Label>
                                <Input
                                    id="bfm-name"
                                    ref={nameFieldRef}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Priya Sharma"
                                    className="mt-1"
                                    data-testid="landing-booking-name"
                                />
                            </div>
                            <div>
                                <Label htmlFor="bfm-phone" className="text-[12px] font-semibold text-slate-700">Phone (for OTP) *</Label>
                                <Input
                                    id="bfm-phone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="10-digit mobile number"
                                    inputMode="tel"
                                    className="mt-1"
                                    data-testid="landing-booking-phone"
                                />
                                <div className="text-[11px] text-slate-500 mt-1">
                                    We&apos;ll send a one-time code to confirm your booking. No spam.
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="bfm-email" className="text-[12px] font-semibold text-slate-700">Email <span className="text-slate-400 font-normal">(optional)</span></Label>
                                <Input
                                    id="bfm-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="mt-1"
                                    data-testid="landing-booking-email"
                                />
                            </div>
                            <div>
                                <Label htmlFor="bfm-notes" className="text-[12px] font-semibold text-slate-700">Anything {firstName} should know? <span className="text-slate-400 font-normal">(optional)</span></Label>
                                <Textarea
                                    id="bfm-notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Concerns, past treatments, questions…"
                                    rows={3}
                                    className="mt-1 resize-none"
                                    data-testid="landing-booking-notes"
                                />
                            </div>
                            <label className="flex items-start gap-2 cursor-pointer" data-testid="landing-booking-consent-row">
                                <input
                                    type="checkbox"
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 rounded border-slate-300 shrink-0"
                                    style={{ accentColor: accent.base }}
                                    data-testid="landing-booking-consent"
                                />
                                <span className="text-[12px] text-slate-600 leading-snug select-none">
                                    I agree to be contacted by Invisique™ via call, SMS, or WhatsApp about my smile consultation. I understand I can opt out anytime.
                                </span>
                            </label>
                        </div>
                    </div>
                )}

                {/* FOOTER — step-appropriate action buttons */}
                <div className="p-4 sm:p-5 border-t border-slate-100 bg-white flex items-center justify-between gap-3 shrink-0">
                    {step === 1 ? (
                        <>
                            <div className="text-[12px] text-slate-500 flex items-center gap-1.5">
                                <ShieldCheck size={13} weight="duotone" style={{ color: accent.base }} />
                                <span className="hidden sm:inline">Free to reserve · Cancel up to 4h before</span>
                                <span className="sm:hidden">Free · OTP-verified</span>
                            </div>
                            <Button
                                onClick={goToStep2}
                                disabled={!selectedTime}
                                className="h-11 px-6 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                                style={selectedTime ? { backgroundColor: accent.base, color: "white" } : undefined}
                                data-testid="landing-booking-continue"
                            >
                                {selectedTime ? `Continue · ${formatSlotTime(selectedTime)}` : "Pick a time to continue"}
                                <ArrowRight size={14} className="ml-1" />
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="ghost"
                                onClick={backToStep1}
                                className="text-[13px]"
                                data-testid="landing-booking-back"
                            >
                                <CaretLeft size={13} className="mr-0.5" /> Back
                            </Button>
                            <Button
                                onClick={confirm}
                                disabled={!canConfirm}
                                className="h-11 px-6 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                                style={canConfirm ? { backgroundColor: accent.base, color: "white" } : undefined}
                                data-testid="landing-booking-confirm"
                            >
                                {submitting ? "Sending OTP…" : (
                                    <>
                                        <PaperPlaneRight size={14} weight="fill" className="mr-1.5" /> Send OTP & Confirm
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function StepBadge({ n, active, done, accent }) {
    const bg = done ? accent.base : (active ? accent.base : "hsl(215 20% 92%)");
    const fg = (active || done) ? "white" : "hsl(215 20% 40%)";
    return (
        <div
            className="h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold"
            style={{ backgroundColor: bg, color: fg }}
        >
            {done ? <CheckCircle size={12} weight="fill" /> : n}
        </div>
    );
}
